import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, Icon, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Switch, FormControlLabel, TablePagination, Select, Checkbox, Chip, InputLabel, Slider, Tooltip, Radio, Menu, Badge, Divider, ClickAwayListener, Button, TextField, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { Form, Formik } from "formik";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { get, postNoResponse, post, getFile } from "utils/WebService";
import { avisarSolicitacoesAlteradas, enviarComAnexos } from "views/umc/filaSolicitacoes/solicitacaoUtils";
import DevolucaoDiretaDialog from "../DevolucaoDiretaDialog";
import moment from "moment";
import { vencimentoDaParcela, vencimentosDasParcelas } from "utils/vencimentosParcelas";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import SeletorMarcacoes from "./SeletorMarcacoes";
import NovaFichaHonorariosDialog from "./NovaFichaHonorariosDialog";
import MDSnackbar from "components/MDSnackbar";
import { toCurrency } from "utils/NumberUtils";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import { useMinimizedWindows } from "context/MinimizedWindowsContext";
import { podeDevolverOuMigrarDireto } from "utils/Perfis";
import { ESCURO, TEXTO_COLORIDO_ESCURO, coresDoTema, corRoxo } from "assets/coresTema";
import { ehCarteiraDeCobranca, ehCarteiraJuridica } from "utils/Carteiras";
import PacoteJuridicoMigracao, { PACOTE_VAZIO, pacoteParaEnvio } from "views/umc/filaSolicitacoes/PacoteJuridicoMigracao";

const formatCurrencyInput = (value) => {
    if (value === null || value === undefined || value === "") return "";
    let isNegative = value.toString().includes("-");
    if (typeof value === "number") {
        value = Math.abs(value).toFixed(2);
    }
    let numericValue = value.toString().replace(/\D/g, "");
    if (!numericValue) return "";
    numericValue = (numericValue / 100).toFixed(2) + "";
    numericValue = numericValue.replace(".", ",");
    numericValue = numericValue.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
    return (isNegative ? "-R$ " : "R$ ") + numericValue;
};

// Valor com sinal explícito ("+R$ 10,00" / "-R$ 142,63"): a correção pelo índice pode ser negativa
const toCurrencyComSinal = (value) => {
    const n = parseFloat(value) || 0;
    return (n < 0 ? "-" : "+") + toCurrency(Math.abs(n));
};

// Divide um valor em parcelas de centavos inteiros: a última fica com a sobra do arredondamento, para a soma bater com o
// total (antes cada parcela ia como total / quantidade e o backend arredondava uma a uma, perdendo centavos)
const dividirEmParcelas = (total, qtd) => {
    const n = Math.max(1, Number(qtd) || 1);
    const centavos = Math.round((Number(total) || 0) * 100);
    const base = Math.floor(centavos / n);
    return Array.from({ length: n }, (_, i) => (i === n - 1 ? centavos - base * (n - 1) : base) / 100);
};
const arredondarCentavos = (v) => Math.round((Number(v) || 0) * 100) / 100;

const formatUncurrency = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === 'number') return value;
    let isNegative = value.toString().includes("-");
    let cleanVal = value.toString().replace("-", "").replace("R$ ", "").trim();
    if (cleanVal.includes(",")) {
        cleanVal = cleanVal.replace(/\./g, "").replace(",", ".");
    }
    let num = parseFloat(cleanVal);
    return isNegative ? -num : num;
};

export default function Contratos({ formRef, recordId, idDevedorCarteira, idDevedorCarteiraRaw, selectedFicha, isViewOnly, pendingContratos = [], setPendingContratos, isHonorariosTab = false, changeTab, onDataUpdated, refreshKey, preloadedContratos, preloadedAcordos, preloadedDevedor, isFila = false, onFichaHonorariosCriada }) {
    const isTerceiroFicha = Boolean(
        formRef?.current?.values?.idDevedorPai ||
        preloadedDevedor?.idDevedorPai ||
        preloadedDevedor?.nomeDevedorPai ||
        preloadedDevedor?.documentoDevedorPai
    );
    const { userPerfils, userFunctionalities } = useUser();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { minimizeWindow, removeWindow } = useMinimizedWindows();
    // Devolver/reativar/migrar direto; sem a permissão, a ação vira solicitação para a Fila de Solicitações
    const isDirectMigrateUser = podeDevolverOuMigrarDireto(userPerfils, userFunctionalities);
    const isAdminOrSuporte = isDirectMigrateUser;
    // Coluna Ações da tabela só com botão para mostrar: editar/excluir (quem edita) ou devolver/reativar direto
    const mostraColunaAcoes = (!isViewOnly && !isFila) || (!!recordId && isAdminOrSuporte);
    const isFichaDevolvida = formRef?.current?.values?.status === 1;
    // Nova ficha de honorários (antes só pela aba Honorários, que saiu da tela): quem pode incluir na Base de Dados
    const podeCriarFichaHonorarios = Boolean(userFunctionalities?.includes("c050_C"));
    const [novaFichaHonorariosAberta, setNovaFichaHonorariosAberta] = useState(false);

    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSavingForm, setIsSavingForm] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedContrato, setSelectedContrato] = useState(null);
    // Hora em que o formulário abriu: com moment() direto no initialValues, o enableReinitialize zerava o formulário quando virava o minuto
    const [aberturaDialog, setAberturaDialog] = useState("");
    const [apenasAtivos, setApenasAtivos] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const [devolucaoDireta, setDevolucaoDireta] = useState(null);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    const [simulacaoOpen, setSimulacaoOpen] = useState(false);
    const [simulacaoMinimizada, setSimulacaoMinimizada] = useState(false);
    const [acordoMinimizado, setAcordoMinimizado] = useState(false);
    const [openExportCronogramaModal, setOpenExportCronogramaModal] = useState(false);
    const [exportDataPrimeiraParcela, setExportDataPrimeiraParcela] = useState("");
    const [simulacaoData, setSimulacaoData] = useState(null);
    const [simulacaoLoading, setSimulacaoLoading] = useState(false);
    const [dataBaseSimulacao, setDataBaseSimulacao] = useState(moment().format("YYYY-MM-DD"));

    const [reagruparModalOpen, setReagruparModalOpen] = useState(false);
    const [reagruparDestinoId, setReagruparDestinoId] = useState("");
    const [reagruparMoverTodos, setReagruparMoverTodos] = useState(true);
    const [reagruparMotivo, setReagruparMotivo] = useState("");
    const [isSubmittingReagrupamento, setIsSubmittingReagrupamento] = useState(false);
    const [fichasDisponiveis, setFichasDisponiveis] = useState([]);

    const [acordoModalOpen, setAcordoModalOpen] = useState(false);
    const [acordoOpcaoSelecionada, setAcordoOpcaoSelecionada] = useState(null);
    const [acordoQtdParcelas, setAcordoQtdParcelas] = useState(1);
    const [acordoPrimeiroVencimento, setAcordoPrimeiroVencimento] = useState(moment().add(30, 'days').format("YYYY-MM-DD"));
    const [acordoPeriodicidade, setAcordoPeriodicidade] = useState(30);
    const [acordoValorEntrada, setAcordoValorEntrada] = useState("");
    const [acordoQtdEntrada, setAcordoQtdEntrada] = useState(1);
    const [acordoVencimentoEntrada, setAcordoVencimentoEntrada] = useState(moment().format("YYYY-MM-DD"));
    const [acordoParcelasGeradas, setAcordoParcelasGeradas] = useState([]);
    const [cronogramaCalculado, setCronogramaCalculado] = useState(false);
    // Troca do 1º vencimento refaz a simulação para a nova data: enquanto não volta, não dá para calcular nem gerar
    const [resimulandoVencimento, setResimulandoVencimento] = useState(false);
    const resimulacaoSeq = useRef(0);
    const [acordoEntradasDetalhadas, setAcordoEntradasDetalhadas] = useState([]);
    const [acordoMleList, setAcordoMleList] = useState([]);
    const [acordoComEntrada, setAcordoComEntrada] = useState(false);
    const [acordoIsExcecao, setAcordoIsExcecao] = useState(false);
    // Motivo da Exceção da Campanha (obrigatório quando o acordo sai fora das regras; vai na ocorrência do acordo)
    const [acordoMotivoExcecao, setAcordoMotivoExcecao] = useState("");
    const [acordoFormaPagamento, setAcordoFormaPagamento] = useState("Boleto");
    const [acordoValorManual, setAcordoValorManual] = useState("");
    const [acordoLoading, setAcordoLoading] = useState(false);
    const [acordos, setAcordos] = useState([]);

    useEffect(() => {
        return () => {
            removeWindow(`simulacao-${recordId || "current"}`);
            removeWindow(`acordo-${recordId || "current"}`);
        };
    }, [recordId, removeWindow]);

    const handleMinimizarSimulacao = () => {
        setSimulacaoMinimizada(true);
        minimizeWindow({
            id: `simulacao-${recordId || "current"}`,
            title: "Simulação de Campanha",
            subtitle: simulacaoData?.devedorNome ? `Devedor: ${simulacaoData.devedorNome}` : "Cálculo de proposta",
            icon: "calculate",
            color: "#1A73E8",
            tabKey: isFila ? "fila-trabalho" : "baseDeDados",
            onRestore: () => {
                setSimulacaoMinimizada(false);
            },
            onClose: () => {
                setSimulacaoMinimizada(false);
                setSimulacaoOpen(false);
            }
        });
    };

    const handleMinimizarAcordo = () => {
        setAcordoMinimizado(true);
        minimizeWindow({
            id: `acordo-${recordId || "current"}`,
            title: "Negociação de Acordo",
            subtitle: simulacaoData?.devedorNome ? `Devedor: ${simulacaoData.devedorNome}` : "Cronograma de parcelas",
            icon: "handshake",
            color: "#4CAF50",
            tabKey: isFila ? "fila-trabalho" : "baseDeDados",
            onRestore: () => {
                setAcordoMinimizado(false);
            },
            onClose: () => {
                setAcordoMinimizado(false);
                setAcordoModalOpen(false);
            }
        });
    };

    // Honorários Configuration State
    const [gerarHO, setGerarHO] = useState(false);
    // O % vem da campanha (simulação) e fica travado; a Exceção libera outro %, com o motivo (vai na ocorrência do acordo)
    const [hoPercentual, setHoPercentual] = useState("");
    const [hoExcecao, setHoExcecao] = useState(false);
    const [hoMotivo, setHoMotivo] = useState("");
    const [hoTipo, setHoTipo] = useState(1);
    const hoTiposList = [
        { id: 1, label: 'Sucumbenciais' },
        { id: 2, label: 'Contratuais' },
        { id: 3, label: 'Iniciais' },
        { id: 4, label: 'Finais' },
        { id: 5, label: 'Outros' }
    ];

    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const [pendingOpcao, setPendingOpcao] = useState(null);
    // Acordos ativos que o novo acordo vai cancelar ({ acordos, ids } dos contratos negociados), para o aviso
    const [acordosSobrepostos, setAcordosSobrepostos] = useState({ acordos: [], ids: [] });
    // Ficha completa com parte dos contratos em acordo: pergunta antes de simular ({ fora, dentro, acordos })
    const [escolhaAcordo, setEscolhaAcordo] = useState(null);
    // O usuário já escolheu incluir o contrato do acordo nesta simulação: não pergunta de novo ao abrir o acordo
    const [renegociacaoConfirmada, setRenegociacaoConfirmada] = useState(false);
    const [selectedContratos, setSelectedContratos] = useState([]);
    // Mudou a seleção depois da simulação: a escolha de incluir o contrato do acordo não vale mais
    useEffect(() => setRenegociacaoConfirmada(false), [selectedContratos]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [contratoToDelete, setContratoToDelete] = useState(null);

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelectedContratos(contratosFiltrados.map(c => c.id));
        } else {
            setSelectedContratos([]);
        }
    };

    const handleCheckboxClick = (event, id) => {
        event.stopPropagation();
        setSelectedContratos(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const fetchContratos = () => {
        if (!recordId) return;

        console.log("=== DEBUG CONTRATOS ===");
        console.log("preloadedContratos recebido:", preloadedContratos ? preloadedContratos.length : "null");
        console.log("idDevedorCarteira filtro:", idDevedorCarteira);
        console.log("isHonorariosTab:", isHonorariosTab);

        if (preloadedContratos !== undefined && preloadedAcordos !== undefined) {
            // Usa dados pré-carregados do componente pai!
            let resContratos = preloadedContratos || [];

            if (resContratos.length > 0) {
                console.log("Amostra IDs devedorCarteira originais:", JSON.stringify(resContratos.map(c => c.idDevedorCarteira)));
            }

            const isFichaHO = Boolean(isHonorariosTab || selectedFicha?.isHonorario === true || selectedFicha?.cbIsHonorario === true);

            if (isFichaHO) {
                resContratos = resContratos.filter(c => c.isHonorario === true || c.cbIsHonorario === true);
            } else {
                const isFilhoFicha = isTerceiroFicha || Boolean(preloadedDevedor?.idDevedorPai);
                resContratos = resContratos.filter(c =>
                    isFilhoFicha || c.isTerceiro === true || (c.isHonorario !== true && c.cbIsHonorario !== true)
                );
            }
            console.log("Após filtro isHonorario, sobrou:", resContratos.length);

            const filterContratosByCarteira = (list) => {
                if (!Array.isArray(list)) return [];
                const targetRaw = idDevedorCarteiraRaw != null 
                    ? String(idDevedorCarteiraRaw) 
                    : (selectedFicha?.rawId != null ? String(selectedFicha.rawId) : null);
                const targetEnc = idDevedorCarteira ? String(idDevedorCarteira) : (selectedFicha?.id ? String(selectedFicha.id) : null);

                if (!targetRaw && !targetEnc) return list;

                const hasAnyLinkedFicha = list.some(c => c.rawIdDevedorCarteira || c.idDevedorCarteira);

                return list.filter(c => {
                    const cRaw = c.rawIdDevedorCarteira != null ? String(c.rawIdDevedorCarteira) : null;
                    const cEnc = c.idDevedorCarteira != null ? String(c.idDevedorCarteira) : null;

                    if (targetRaw && (cRaw === targetRaw || cEnc === targetRaw)) return true;
                    if (targetEnc && (cEnc === targetEnc || cRaw === targetEnc)) return true;

                    if (!cRaw && !cEnc) {
                        if (selectedFicha) {
                            if (c.idCarteira && selectedFicha.idCarteira && String(c.idCarteira) === String(selectedFicha.idCarteira)) return true;
                            if (c.nomeCarteira && selectedFicha.nomeCarteira && c.nomeCarteira.trim().toLowerCase() === selectedFicha.nomeCarteira.trim().toLowerCase()) return true;
                        }
                        if (!hasAnyLinkedFicha) return true;
                    }
                    return false;
                });
            };

            resContratos = filterContratosByCarteira(resContratos);

            setContratos(resContratos);
            setAcordos(preloadedAcordos || []);
            return;
        }

        setLoading(true);
        Promise.all([
            get(`/contratos/list/${recordId}`),
            get(`/acordo/list/${recordId}`)
        ]).then(([contratosRes, acordosRes]) => {
            let resContratos = contratosRes || [];
            const isFichaHO = Boolean(isHonorariosTab || selectedFicha?.isHonorario === true || selectedFicha?.cbIsHonorario === true);

            if (isFichaHO) {
                resContratos = resContratos.filter(c => c.isHonorario === true || c.cbIsHonorario === true);
            } else {
                const isFilhoFicha = isTerceiroFicha || Boolean(preloadedDevedor?.idDevedorPai);
                resContratos = resContratos.filter(c =>
                    isFilhoFicha || c.isTerceiro === true || (c.isHonorario !== true && c.cbIsHonorario !== true)
                );
            }
            const filterContratosByCarteira = (list) => {
                if (!Array.isArray(list)) return [];
                const targetRaw = idDevedorCarteiraRaw != null 
                    ? String(idDevedorCarteiraRaw) 
                    : (selectedFicha?.rawId != null ? String(selectedFicha.rawId) : null);
                const targetEnc = idDevedorCarteira ? String(idDevedorCarteira) : (selectedFicha?.id ? String(selectedFicha.id) : null);

                if (!targetRaw && !targetEnc) return list;

                const hasAnyLinkedFicha = list.some(c => c.rawIdDevedorCarteira || c.idDevedorCarteira);

                return list.filter(c => {
                    const cRaw = c.rawIdDevedorCarteira != null ? String(c.rawIdDevedorCarteira) : null;
                    const cEnc = c.idDevedorCarteira != null ? String(c.idDevedorCarteira) : null;

                    if (targetRaw && (cRaw === targetRaw || cEnc === targetRaw)) return true;
                    if (targetEnc && (cEnc === targetEnc || cRaw === targetEnc)) return true;

                    if (!cRaw && !cEnc) {
                        if (selectedFicha) {
                            if (c.idCarteira && selectedFicha.idCarteira && String(c.idCarteira) === String(selectedFicha.idCarteira)) return true;
                            if (c.nomeCarteira && selectedFicha.nomeCarteira && c.nomeCarteira.trim().toLowerCase() === selectedFicha.nomeCarteira.trim().toLowerCase()) return true;
                        }
                        if (!hasAnyLinkedFicha) return true;
                    }
                    return false;
                });
            };
            resContratos = filterContratosByCarteira(resContratos);
            setContratos(resContratos);
            setAcordos(acordosRes || []);
        }).catch(() => {
            showSnackbar("Erro ao carregar contratos ou acordos", false);
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchContratos();
    }, [recordId, refreshKey, idDevedorCarteira, idDevedorCarteiraRaw, selectedFicha, preloadedContratos, preloadedAcordos, preloadedDevedor]);

    // Devedor novo: os contratos ficam só na tela até "Salvar Cadastro" (antes a tabela ficava vazia depois de incluir)
    useEffect(() => {
        if (!recordId) setContratos(pendingContratos || []);
    }, [recordId, pendingContratos]);

    const invalidateCronograma = () => {
        setCronogramaCalculado(false);
        setAcordoParcelasGeradas([]);
    };

    const handleChangePrimeiroVencimento = (valStr) => {
        setAcordoPrimeiroVencimento(valStr);
        invalidateCronograma();
        const seq = ++resimulacaoSeq.current;
        if (!valStr || valStr.length < 10) {
            setResimulandoVencimento(false);
            return;
        }
        setResimulandoVencimento(true);

        let query = `dataBase=${valStr}`;
        if (selectedContratos.length > 0) {
            query += `&contratosIds=${selectedContratos.join(',')}`;
        } else {
            const allIds = contratos.map(c => c.id).join(',');
            query += `&contratosIds=${allIds}`;
        }

        get(`/campanha/simular-calculo-devedor/${recordId}?${query}`)
            .then(res => {
                // Resposta de uma data que já foi trocada de novo: a da última troca é que vale
                if (seq !== resimulacaoSeq.current) return;
                setSimulacaoData(res);
                // Parcelas calculadas antes desta resposta usaram o valor da data anterior
                invalidateCronograma();
                setAcordoOpcaoSelecionada(prev => {
                    if (!prev) return prev;
                    if (prev.isManual) {
                        setAcordoValorManual(formatCurrencyInput(res.totalAtualizado));
                        return { ...prev, totalComDesconto: res.totalAtualizado };
                    }
                    if (res.opcoesParcelamento) {
                        const newOption = res.opcoesParcelamento.find(o => o.parcelaMinima === prev.parcelaMinima && o.parcelaMaxima === prev.parcelaMaxima);
                        return newOption || prev;
                    }
                    return prev;
                });
            })
            .catch(err => {
                if (seq !== resimulacaoSeq.current) return;
                showSnackbar(err.message || "Erro ao re-simular com a nova data de vencimento.", false);
            })
            .finally(() => {
                if (seq === resimulacaoSeq.current) setResimulandoVencimento(false);
            });
    };

    const handleMainValorEntradaChange = (valStr) => {
        const formatted = formatCurrencyInput(valStr);
        setAcordoValorEntrada(formatted);
        invalidateCronograma();
        const val = formatUncurrency(formatted) || 0;
        const qty = parseInt(acordoQtdEntrada) || 0;
        if (qty > 0) {
            const valEach = val / qty;
            setAcordoEntradasDetalhadas(prev => {
                const list = [...prev];
                return list.map(item => ({
                    ...item,
                    valor: formatCurrencyInput(valEach)
                }));
            });
        }
    };

    useEffect(() => {
        if (!acordoComEntrada) {
            setAcordoEntradasDetalhadas([]);
            return;
        }
        const qty = parseInt(acordoQtdEntrada) || 0;
        if (qty <= 0) {
            setAcordoEntradasDetalhadas([]);
            return;
        }

        setAcordoEntradasDetalhadas(prev => {
            if (qty === 1) {
                const existingMle = prev[0]?.isMle || false;
                return [{
                    valor: acordoValorEntrada,
                    vencimento: acordoVencimentoEntrada,
                    isMle: existingMle
                }];
            } else {
                if (prev.length !== qty) {
                    const baseVal = formatUncurrency(acordoValorEntrada) || 0;
                    const valEach = qty > 0 ? baseVal / qty : 0;
                    const list = [];
                    const referencia = moment(acordoVencimentoEntrada || undefined);

                    for (let i = 0; i < qty; i++) {
                        if (prev[i]) {
                            list.push(prev[i]);
                        } else {
                            list.push({
                                valor: formatCurrencyInput(valEach),
                                vencimento: vencimentoDaParcela(referencia, i, acordoPeriodicidade).format("YYYY-MM-DD"),
                                isMle: false
                            });
                        }
                    }
                    return list;
                }
                return prev;
            }
        });
    }, [acordoComEntrada, acordoQtdEntrada, acordoValorEntrada, acordoVencimentoEntrada, acordoPeriodicidade]);

    const handleOpenDialog = (contrato = null) => {
        setSelectedContrato(contrato);
        setAberturaDialog(moment().format("DD/MM/YYYY HH:mm"));
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setSelectedContrato(null);
        setOpenDialog(false);
    };

    const handleSubmit = (values, { setSubmitting }) => {
        // Sem carteira o contrato não tem ficha (no devedor novo, a ficha nem é criada)
        if (!values.carteiraId) {
            showSnackbar("Escolha a carteira do contrato.", false);
            setSubmitting(false);
            return;
        }
        const payload = {
            id: values.id,
            idDevedor: recordId,
            titulo: values.titulo,
            descricao: values.descricao,
            parcela: values.parcela,
            // Mês/ano de referência do título no cliente (UMC); vazio = sem referência
            mesReferencia: values.mesReferencia ? Number(values.mesReferencia) : null,
            anoReferencia: values.anoReferencia ? Number(values.anoReferencia) : null,
            vencimento: values.vencimento,
            valorOriginal: formatUncurrency(values.valorOriginal),
            saldo: formatUncurrency(values.saldo),
            // Vazio = fica como está (o backend só muda com o campo preenchido)
            porConta: formatUncurrency(values.porConta),
            situacao: values.situacao,
            marcacoesIds: values.marcacoesIds,
            idCarteira: values.carteiraId,
            // Nome junto: o contrato do devedor novo fica só na tela até salvar, e a coluna Carteira mostrava "-"
            nomeCarteira: carteirasOptions.find(c => String(c.id) === String(values.carteiraId))?.nome || null,
            isHonorario: isHonorariosTab,
            idDevedorCarteira: idDevedorCarteira || null,
        };

        if (!recordId) {
            if (values.id) {
                payload.id = values.id;
                const updated = pendingContratos.map(c => c.id === values.id ? payload : c);
                setPendingContratos(updated);
            } else {
                payload.id = `temp_${Date.now()}`;
                setPendingContratos([...pendingContratos, payload]);
            }
            showSnackbar("Contrato salvo provisoriamente!", true);
            handleCloseDialog();
            setSubmitting(false);
            return;
        }

        postNoResponse("/contratos/save", payload)
            .then(() => {
                showSnackbar("Contrato salvo com sucesso!", true);
                // Recarrega a ficha: a lista vem da tela de cima (preloadedContratos), e só o fetchContratos mostrava
                // a lista antiga (o contrato era gravado, mas não aparecia até reabrir a ficha)
                if (onDataUpdated) onDataUpdated();
                // O contrato pode ter criado ficha (de honorários ou de outra carteira): o topo só relê com este aviso
                window.dispatchEvent(new Event("devedorStatusUpdated"));
                fetchContratos();
                handleCloseDialog();
            })
            .catch((err) => showSnackbar(err?.message ? `Erro ao salvar contrato: ${err.message}` : "Erro ao salvar contrato", false))
            .finally(() => setSubmitting(false));
    };

    const handleReativarContratoDireto = (contrato) => {
        setConfirmAction({
            open: true,
            title: "Reativar Contrato",
            message: `Deseja realmente reativar o contrato ${contrato.titulo}?`,
            onConfirm: () => {
                postNoResponse(`/contratos/reativar/${contrato.id}`, {})
                    .then(() => {
                        showSnackbar("Contrato reativado com sucesso!", true);
                        if (onDataUpdated) onDataUpdated();
                        fetchContratos();
                    })
                    .catch(err => {
                        showSnackbar("Erro ao reativar contrato", false);
                    });
            }
        });
    };

    const handleReativarLote = () => {
        const reativarIds = selectedContratos.filter(id => {
            const c = contratos.find(x => x.id === id);
            return c && c.situacao === 4;
        });
        if (reativarIds.length === 0) {
            showSnackbar("Nenhum contrato devolvido selecionado para reativar.", false);
            return;
        }
        setConfirmAction({
            open: true,
            title: "Reativar Selecionados",
            message: `Deseja reativar os ${reativarIds.length} contratos devolvidos selecionados?`,
            onConfirm: () => {
                postNoResponse(`/contratos/reativar-lote`, reativarIds)
                    .then(() => {
                        showSnackbar("Contratos reativados com sucesso!", true);
                        if (onDataUpdated) onDataUpdated();
                        fetchContratos();
                        setSelectedContratos([]);
                    })
                    .catch(err => {
                        showSnackbar("Erro ao reativar contratos", false);
                    });
            }
        });
    };

    const handleDeleteClick = (contrato) => {
        setContratoToDelete(contrato);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!contratoToDelete) return;

        if (!recordId || contratoToDelete.id.toString().startsWith("temp_")) {
            const updatedPending = pendingContratos.filter(c => c.id !== contratoToDelete.id);
            setPendingContratos(updatedPending);
            setContratos(updatedPending);
            showSnackbar("Contrato removido com sucesso!", true);
            setDeleteDialogOpen(false);
            setContratoToDelete(null);
            return;
        }

        postNoResponse("/contratos/delete", { id: contratoToDelete.id })
            .then(() => {
                showSnackbar("Contrato excluído com sucesso!", true);
                if (onDataUpdated) onDataUpdated();
                fetchContratos();
            })
            .catch(() => {
                showSnackbar("Erro ao excluir contrato. Pode haver acordos vinculados.", false);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setContratoToDelete(null);
            });
    };

    // Pergunta "deseja devolver?" e depois a tela de texto/anexo que vão junto da ocorrência (DevolucaoDiretaDialog)
    const handleDevolverDireto = (contrato) => {
        setDevolucaoDireta({
            titulo: "Devolver Contrato",
            pergunta: `Deseja realmente devolver o contrato/título ${contrato.titulo}?`,
            endpoint: `/contratos/devolver/${contrato.id}`,
            idDevedor: recordId,
        });
    };

    const aoConcluirDevolucaoDireta = (avisoAnexo) => {
        showSnackbar("Contrato devolvido com sucesso!" + avisoAnexo, !avisoAnexo);
        if (onDataUpdated) onDataUpdated();
        fetchContratos();
    };

    const getSituacaoLabel = (sit) => {
        switch (sit) {
            case 1: return "Aberto";
            case 2: return "Baixado Parcial";
            case 3: return "Liquidado";
            case 4: return "Devolvida";
            // Zerado na migração para o Jurídico (o débito do processo entra à parte)
            case 6: return "Migrado";
            default: return "Aberto";
        }
    };

    const handleSimularCalculoDevedor = () => {
        if (isFichaDevolvida) {
            alert("Não é possível simular acordos para fichas Devolvidas.");
            return;
        }
        if (contratos.length === 0) {
            showSnackbar("Não há contratos para simular.", false);
            return;
        }
        // Ficha completa com só parte dos contratos num acordo ativo: pergunta antes se negocia só os de fora
        if (selectedContratos.length === 0) {
            const ativos = contratos.filter(c => !c.situacao || c.situacao === 1 || c.situacao === 2);
            const noAcordo = acordosAtivosCom(ativos.map(c => c.id));
            const idsNoAcordo = new Set(noAcordo.flatMap(a => (a.contratos || []).map(c => c.idContrato)));
            const fora = ativos.filter(c => !idsNoAcordo.has(c.id));
            const dentro = ativos.filter(c => idsNoAcordo.has(c.id));
            if (dentro.length > 0 && fora.length > 0) {
                setEscolhaAcordo({ fora, dentro, acordos: noAcordo });
                return;
            }
        }
        simularContratos(selectedContratos.length > 0 ? selectedContratos : contratos.map(c => c.id), false);
    };

    // Acordos ativos que já têm algum destes contratos: o novo acordo os cancela ao ser gerado
    const acordosAtivosCom = (idsContratos) => acordos.filter(a =>
        a.situacao === 1 && (a.contratos || []).some(c => idsContratos.includes(c.idContrato)));

    const simularContratos = (idsContratos, renegociar) => {
        setRenegociacaoConfirmada(renegociar);
        setSimulacaoLoading(true);
        const query = `dataBase=${dataBaseSimulacao}&contratosIds=${idsContratos.join(',')}`;

        get(`/campanha/simular-calculo-devedor/${recordId}?${query}`)
            .then(res => {
                setSimulacaoData(res);
                setSimulacaoOpen(true);
                setSimulacaoMinimizada(false);
            })
            .catch(err => {
                showSnackbar(err.message || "Erro ao simular cálculo ou nenhuma campanha ativa.", false);
            })
            .finally(() => setSimulacaoLoading(false));
    };

    const proceedOpenAcordoModal = (opcao) => {
        setAcordoOpcaoSelecionada(opcao);
        setAcordoQtdParcelas(opcao.parcelaMinima || 1);
        setAcordoPrimeiroVencimento(dataBaseSimulacao);
        setAcordoPeriodicidade(30);
        setAcordoValorEntrada("");
        setAcordoQtdEntrada(1);
        setAcordoVencimentoEntrada(moment(dataBaseSimulacao).format("YYYY-MM-DD"));
        setAcordoEntradasDetalhadas([]);
        setAcordoMleList([]);
        setAcordoComEntrada(false);
        setAcordoIsExcecao(false);
        setAcordoMotivoExcecao("");
        if (opcao.formaPagamentoDescricao && opcao.formaPagamentoDescricao !== "Todas") {
            setAcordoFormaPagamento(opcao.formaPagamentoDescricao);
        } else {
            setAcordoFormaPagamento("Boleto");
        }
        setAcordoValorManual("");
        setAcordoParcelasGeradas([]);
        setCronogramaCalculado(false);
        setAcordoModalOpen(true);
        setAcordoMinimizado(false);
    };

    const handleOpenAcordoModal = (opcao) => {
        if (isFichaDevolvida) {
            alert("Não é possível simular acordos para fichas Devolvidas.");
            return;
        }
        if (avisarAcordoAtivo(opcao)) return;
        proceedOpenAcordoModal(opcao);
    };

    /**
     * Algum contrato da negociação já está num acordo ativo: mostra qual acordo será cancelado ao gerar o novo (a menos
     * que o usuário já tenha escolhido incluir esse contrato antes de simular). Devolve true se abriu o aviso.
     */
    const avisarAcordoAtivo = (opcao) => {
        const checkContratos = selectedContratos.length > 0 ? selectedContratos : contratos.map(c => c.id);
        const sobrepostos = acordosAtivosCom(checkContratos);
        if (sobrepostos.length === 0 || renegociacaoConfirmada) return false;
        setAcordosSobrepostos({ acordos: sobrepostos, ids: checkContratos });
        setPendingOpcao(opcao);
        setConfirmCancelOpen(true);
        return true;
    };

    const proceedOpenAcordoManual = () => {
        const regra1x = (simulacaoData?.opcoesParcelamento || []).find(r => 1 >= r.parcelaMinima && 1 <= r.parcelaMaxima);
        const valorInicial = regra1x ? regra1x.totalComDesconto : simulacaoData.totalAtualizado;

        setAcordoOpcaoSelecionada({
            isManual: true,
            totalComDesconto: valorInicial,
            parcelaMinima: 1,
            parcelaMaxima: 999
        });
        setAcordoQtdParcelas(1);
        setAcordoPrimeiroVencimento(dataBaseSimulacao);
        setAcordoPeriodicidade(30);
        setAcordoValorEntrada("");
        setAcordoQtdEntrada(1);
        setAcordoVencimentoEntrada(moment().format("YYYY-MM-DD"));
        setAcordoEntradasDetalhadas([]);
        setAcordoMleList([]);
        setAcordoParcelasGeradas([]);
        setCronogramaCalculado(false);
        setAcordoComEntrada(false);
        setAcordoIsExcecao(false);
        setAcordoMotivoExcecao("");
        setAcordoFormaPagamento("Boleto");
        setAcordoValorManual(formatCurrencyInput(valorInicial));
        setAcordoModalOpen(true);
        setAcordoMinimizado(false);
    };

    const handleOpenAcordoManual = () => {
        if (isFichaDevolvida) {
            alert("Não é possível simular acordos para fichas Devolvidas.");
            return;
        }
        if (avisarAcordoAtivo({ isManual: true })) return;
        proceedOpenAcordoManual();
    };

    // Cálculos em tempo real da regra da campanha correspondente à forma de pagamento e quantidade de parcelas
    const regrasFormaPagamento = (simulacaoData?.opcoesParcelamento || []).filter(r => 
        !r.formaPagamentoDescricao || r.formaPagamentoDescricao === "Todas" || r.formaPagamentoDescricao === acordoFormaPagamento
    );
    const maxParcelasCampanhaForma = regrasFormaPagamento.length > 0 
        ? Math.max(...regrasFormaPagamento.map(r => Number(r.parcelaMaxima) || 1))
        : 0;

    const totalParcelasConfiguradas = Math.max(1, Number(acordoQtdParcelas || 1)) + (acordoComEntrada && formatUncurrency(acordoValorEntrada) > 0 ? (Number(acordoQtdEntrada) || 1) : 0);
    const isAvistaAcordo = Number(acordoQtdParcelas || 1) <= 1 && !acordoComEntrada;
    const regraCampanhaAtual = regrasFormaPagamento.find(r => 
        totalParcelasConfiguradas >= r.parcelaMinima && 
        totalParcelasConfiguradas <= r.parcelaMaxima
    );
    const valorMinimoCampanha = regraCampanhaAtual ? regraCampanhaAtual.totalComDesconto : (simulacaoData?.totalAtualizado || 0);
    const valorIntegralCampanha = simulacaoData?.totalAtualizado || 0;
    const valorDigitadoAcordo = formatUncurrency(acordoValorManual) || 0;

    const isValorAbaixoCampanha = Boolean(acordoOpcaoSelecionada?.isManual && !acordoIsExcecao && regraCampanhaAtual && valorDigitadoAcordo < (valorMinimoCampanha - 0.05));
    const isParcelasForaCampanha = Boolean(!acordoIsExcecao && !regraCampanhaAtual && (simulacaoData?.opcoesParcelamento || []).length > 0);
    const isBloqueadoPorRegra = isValorAbaixoCampanha || isParcelasForaCampanha;
    // Exceção da Campanha usada de fato: a chave ligada com o acordo fora das regras (valor abaixo do mínimo, só no valor
    // digitado, ou parcelas fora das faixas). Antes o aviso "valor abaixo do mínimo" aparecia também na opção da campanha,
    // em que o valor não se digita, e nada ia para a ocorrência
    const valorAbaixoComExcecao = Boolean(acordoIsExcecao && acordoOpcaoSelecionada?.isManual && regraCampanhaAtual && valorDigitadoAcordo < (valorMinimoCampanha - 0.05));
    const parcelasForaComExcecao = Boolean(acordoIsExcecao && !regraCampanhaAtual && (simulacaoData?.opcoesParcelamento || []).length > 0);
    const usaExcecaoCampanha = valorAbaixoComExcecao || parcelasForaComExcecao;
    const detalheExcecaoCampanha = [
        valorAbaixoComExcecao ? `valor ${toCurrency(valorDigitadoAcordo)} abaixo do mínimo de ${toCurrency(valorMinimoCampanha)} para ${totalParcelasConfiguradas}x` : null,
        parcelasForaComExcecao ? `${totalParcelasConfiguradas}x em ${acordoFormaPagamento}, fora das faixas da campanha` : null,
    ].filter(Boolean).join("; ");

    const handleCalcularCronograma = () => {
        if (resimulandoVencimento) {
            showSnackbar("Aguarde: os valores estão sendo atualizados para a nova data de vencimento.", false);
            return;
        }
        if (!acordoIsExcecao) {
            if (isParcelasForaCampanha) {
                showSnackbar(`A campanha não prevê parcelamento em ${totalParcelasConfiguradas}x para ${acordoFormaPagamento}${maxParcelasCampanhaForma > 0 ? ` (máximo permitido: ${maxParcelasCampanhaForma}x)` : ""}. Ative 'Liberar como Exceção' ou altere a forma de pagamento para prosseguir.`, false);
                return;
            }
            if (isValorAbaixoCampanha) {
                showSnackbar(`Valor mínimo permitido pela campanha para ${totalParcelasConfiguradas}x é ${toCurrency(valorMinimoCampanha)}. Ative 'Liberar como Exceção' para prosseguir com valor inferior.`, false);
                return;
            }
        }

        let total = acordoOpcaoSelecionada?.isManual ? (formatUncurrency(acordoValorManual) || 0) : (acordoOpcaoSelecionada?.totalComDesconto || 0);

        const qtdParcelasNum = Math.max(1, Number(acordoQtdParcelas || 1));

        // Buscar a opção de parcelamento configurada na campanha correspondente à quantidade de parcelas informada
        const opcaoCorrespondente = regraCampanhaAtual || (simulacaoData?.opcoesParcelamento || []).find(op =>
            qtdParcelasNum >= op.parcelaMinima && qtdParcelasNum <= op.parcelaMaxima
        );

        const pctEntradaConfig = opcaoCorrespondente ? Number(opcaoCorrespondente.percentualMinimoEntrada || opcaoCorrespondente.percentualEntrada || 0) : 0;

        let parcelas = [];
        let numParcela = 0;

        if (acordoComEntrada) {
            // CASO 1: Usuário ativou explicitamente o switch "Possui Pagamento de Entrada?"
            let entrada = formatUncurrency(acordoValorEntrada) || 0;
            if (entrada === 0 && pctEntradaConfig > 0 && total > 0) {
                entrada = Number((total * (pctEntradaConfig / 100)).toFixed(2));
                setAcordoValorEntrada(formatCurrencyInput(entrada));
            }

            let saldo = arredondarCentavos(total - entrada);
            let baseVencimento = moment(acordoPrimeiroVencimento || dataBaseSimulacao);
            // Referência das parcelas depois da entrada e o índice da 1ª delas nessa série (vencimentoDaParcela: mesmo dia do
            // mês na periodicidade mensal, sem domingo); sem entrada, o 1º vencimento informado é a própria 1ª parcela
            let referenciaParcelas = moment(baseVencimento);
            let indiceInicial = 0;

            if (acordoEntradasDetalhadas.length > 0) {
                for (let i = 0; i < acordoEntradasDetalhadas.length; i++) {
                    const ent = acordoEntradasDetalhadas[i];
                    parcelas.push({
                        numeroParcela: ++numParcela,
                        isEntrada: true,
                        isMle: acordoMleList[i] || false,
                        valorParcela: formatUncurrency(ent.valor) || 0,
                        vencimento: ent.vencimento
                    });
                }
                // As parcelas seguem a última entrada (que pode ter sido trocada à mão): a 1ª é um período depois dela
                referenciaParcelas = moment(acordoEntradasDetalhadas[acordoEntradasDetalhadas.length - 1].vencimento);
                indiceInicial = 1;
                baseVencimento = vencimentoDaParcela(referenciaParcelas, 1, acordoPeriodicidade);
                setAcordoPrimeiroVencimento(baseVencimento.format("YYYY-MM-DD"));
            } else if (entrada > 0) {
                const qtdEntradas = acordoQtdEntrada || 1;
                const valoresEntrada = dividirEmParcelas(entrada, qtdEntradas);
                // Entradas e parcelas numa série só, a partir do vencimento da 1ª entrada
                referenciaParcelas = moment(acordoVencimentoEntrada || dataBaseSimulacao);
                for (let i = 0; i < qtdEntradas; i++) {
                    parcelas.push({
                        numeroParcela: ++numParcela,
                        isEntrada: true,
                        isMle: acordoMleList[i] || false,
                        valorParcela: valoresEntrada[i],
                        vencimento: vencimentoDaParcela(referenciaParcelas, i, acordoPeriodicidade).format("YYYY-MM-DD")
                    });
                }
                indiceInicial = qtdEntradas;
                baseVencimento = vencimentoDaParcela(referenciaParcelas, qtdEntradas, acordoPeriodicidade);
                setAcordoPrimeiroVencimento(baseVencimento.format("YYYY-MM-DD"));
            }

            if (qtdParcelasNum > 0) {
                const valores = dividirEmParcelas(saldo, qtdParcelasNum);
                for (let i = 0; i < qtdParcelasNum; i++) {
                    parcelas.push({
                        numeroParcela: ++numParcela,
                        isEntrada: false,
                        valorParcela: valores[i],
                        vencimento: vencimentoDaParcela(referenciaParcelas, indiceInicial + i, acordoPeriodicidade).format("YYYY-MM-DD")
                    });
                }
            }
        } else {
            // CASO 2: Entrada NÃO ativada como linha avulsa. Respeita exatamente a quantidade de parcelas informada (ex: 12 parcelas -> gera exatamente 12 linhas)
            const primeiroVencimento = moment(acordoPrimeiroVencimento || dataBaseSimulacao);
            const vencimentos = vencimentosDasParcelas(primeiroVencimento, qtdParcelasNum, acordoPeriodicidade);

            if (pctEntradaConfig > 0 && total > 0 && qtdParcelasNum > 1) {
                let valP1 = Number((total * (pctEntradaConfig / 100)).toFixed(2));
                let saldoRestante = arredondarCentavos(total - valP1);
                let qtdDemais = qtdParcelasNum - 1; // Ex: se total parcelas é 12, P1 usa o % de entrada e as 11 restantes dividem o saldo!
                const valoresDemais = dividirEmParcelas(saldoRestante, qtdDemais);

                // Parcela 1 (com a % de entrada)
                parcelas.push({
                    numeroParcela: ++numParcela,
                    isEntrada: false,
                    valorParcela: valP1,
                    vencimento: vencimentos[0]
                });

                // Parcela 2 até N
                for (let i = 0; i < qtdDemais; i++) {
                    parcelas.push({
                        numeroParcela: ++numParcela,
                        isEntrada: false,
                        valorParcela: valoresDemais[i],
                        vencimento: vencimentos[i + 1]
                    });
                }
            } else {
                const valores = dividirEmParcelas(total, qtdParcelasNum);
                for (let i = 0; i < qtdParcelasNum; i++) {
                    parcelas.push({
                        numeroParcela: ++numParcela,
                        isEntrada: false,
                        valorParcela: valores[i],
                        vencimento: vencimentos[i]
                    });
                }
            }
        }

        setAcordoParcelasGeradas(parcelas);
        setCronogramaCalculado(true);
    };

    const handleChangeParcela = (idx, field, value) => {
        let novas = [...acordoParcelasGeradas];
        novas[idx][field] = value;

        if (field === 'valorParcela' && idx < novas.length - 1) {
            const totalBase = acordoOpcaoSelecionada?.isManual ? (formatUncurrency(acordoValorManual) || 0) : (acordoOpcaoSelecionada?.totalComDesconto || 0);

            let sumToIdx = 0;
            for (let i = 0; i <= idx; i++) {
                let v = formatUncurrency(novas[i].valorParcela);
                if (v === null || isNaN(v)) v = Number(novas[i].valorParcela);
                if (isNaN(v)) v = 0;
                sumToIdx += v;
            }

            let restante = arredondarCentavos(totalBase - sumToIdx);
            if (restante < 0) restante = 0;

            let parcelsRemaining = novas.length - 1 - idx;
            const valoresRestantes = dividirEmParcelas(restante, parcelsRemaining);

            for (let i = idx + 1; i < novas.length; i++) {
                novas[i].valorParcela = formatCurrencyInput(valoresRestantes[i - idx - 1].toFixed(2));
            }
        }

        setAcordoParcelasGeradas(novas);
    };

    // % de honorários da campanha da simulação (null = a campanha não tem): o campo acompanha, fora da Exceção
    const hoPercentualCampanha = simulacaoData?.percentualHonorariosCampanha ?? null;
    useEffect(() => {
        if (!hoExcecao) setHoPercentual(hoPercentualCampanha !== null ? String(hoPercentualCampanha).replace(".", ",") : "");
    }, [hoPercentualCampanha, hoExcecao]);
    const hoPercentualNumero = parseFloat(String(hoPercentual || "").replace(",", "."));

    const handleGerarAcordo = () => {
        if (resimulandoVencimento) {
            showSnackbar("Aguarde: os valores estão sendo atualizados para a nova data de vencimento.", false);
            return;
        }
        const isAvista = isAvistaAcordo;
        const totalBase = acordoOpcaoSelecionada.isManual ? (formatUncurrency(acordoValorManual) || 0) : acordoOpcaoSelecionada.totalComDesconto;

        if (!isAvista && (!cronogramaCalculado || acordoParcelasGeradas.length === 0)) {
            showSnackbar("Calcule as parcelas antes de confirmar e gerar o acordo.", false);
            return;
        }

        // Verifica regra de campanha
        if (!acordoIsExcecao) {
            if (isParcelasForaCampanha) {
                showSnackbar(`Parcelamento em ${totalParcelasConfiguradas}x não permitido para ${acordoFormaPagamento} pela campanha${maxParcelasCampanhaForma > 0 ? ` (máximo permitido: ${maxParcelasCampanhaForma}x)` : ""}. Ative 'Liberar como Exceção' ou altere a forma de pagamento para prosseguir.`, false);
                return;
            }
            if (isValorAbaixoCampanha) {
                showSnackbar(`Valor mínimo permitido para ${totalParcelasConfiguradas}x pela campanha é ${toCurrency(valorMinimoCampanha)}. Marque 'Liberar como Exceção' se deseja prosseguir com valor inferior.`, false);
                return;
            }
        }
        if (usaExcecaoCampanha && acordoMotivoExcecao.trim().length < 5) {
            showSnackbar("O acordo está fora das regras da campanha: informe o motivo da exceção.", false);
            return;
        }

        // Débito de honorários: o % da campanha ou, pela Exceção, outro com o motivo (o backend confere o mesmo)
        if (!isHonorariosTab && gerarHO) {
            if (!(hoPercentualNumero > 0) || hoPercentualNumero > 100) {
                showSnackbar("Informe o % de honorários sobre o acordo (maior que 0 e até 100).", false);
                return;
            }
            const daCampanha = hoPercentualCampanha !== null && Number(hoPercentualCampanha) === hoPercentualNumero;
            if (!daCampanha && (!hoExcecao || hoMotivo.trim().length < 5)) {
                showSnackbar("Para usar um % de honorários diferente do da campanha, clique em Exceção e informe o motivo.", false);
                return;
            }
        }

        let isSingleParcel = (Number(acordoQtdParcelas) === 1 && !acordoComEntrada);

        let finalParcelas = [];
        if (isAvista || (isSingleParcel && acordoParcelasGeradas.length === 0)) {
            finalParcelas = [{
                numeroParcela: 1,
                valorParcela: totalBase,
                vencimento: acordoPrimeiroVencimento,
                cbEntrada: false,
                cbMle: false
            }];
        } else {
            if (acordoParcelasGeradas.length === 0) {
                showSnackbar("Gere o cronograma primeiro", false);
                return;
            }

            let sum = 0;
            for (let p of acordoParcelasGeradas) {
                let v = formatUncurrency(p.valorParcela);
                if (v === null) v = Number(p.valorParcela);
                sum += v;
            }

            // Centavo a centavo: a soma das parcelas é o que o devedor paga e tem de ser o Total a Pagar do acordo
            if (Math.round(sum * 100) !== Math.round(Number(totalBase || 0) * 100)) {
                showSnackbar(`A soma das parcelas (${toCurrency(arredondarCentavos(sum))}) não confere com o Total a Pagar do acordo (${toCurrency(totalBase)}). Clique em Calcular Parcelas de novo ou ajuste os valores.`, false);
                return;
            }

            finalParcelas = acordoParcelasGeradas.map((p, i) => ({
                numeroParcela: i + 1,
                valorParcela: arredondarCentavos(formatUncurrency(p.valorParcela) !== null ? formatUncurrency(p.valorParcela) : p.valorParcela),
                vencimento: p.vencimento.length === 10 ? p.vencimento + "T12:00:00" : p.vencimento,
                cbEntrada: p.isEntrada || false,
                cbMle: p.isMle || false
            }));
        }

        setAcordoLoading(true);
        const checkContratos = selectedContratos.length > 0 ? selectedContratos : contratos.map(c => c.id);
        const hasActive = acordosAtivosCom(checkContratos).length > 0;

        const payload = {
            idDevedor: recordId,
            cancelarAcordoAtual: hasActive,
            idCampanha: simulacaoData?.campanhaId || null,
            valorOriginalTotal: simulacaoData.valorOriginal,
            valorAcordoTotal: totalBase,
            descontoAplicado: simulacaoData.totalAtualizado - totalBase,
            multaAplicada: simulacaoData.valorMulta,
            jurosAplicados: simulacaoData.valorJuros,
            correcaoAplicada: simulacaoData.valorCorrecao,
            honorariosAplicados: simulacaoData.valorHonorarios,
            formaPagamento: acordoFormaPagamento,
            qtdParcelas: finalParcelas.length,
            primeiroVencimento: finalParcelas[0].vencimento,
            parcelas: finalParcelas,
            contratos: contratos.filter(c => (c.situacao === 1 || c.situacao === 2) && (selectedContratos.length === 0 || selectedContratos.includes(c.id))).map(c => ({
                idContrato: c.id,
                titulo: c.titulo,
                valorNegociado: c.saldo // Simplificado
            })),
            // Força false se estiver na aba de Honorários para evitar gerar débitos duplos
            gerarDividaHonorarios: isHonorariosTab ? false : gerarHO,
            percentualHonorarios: (isHonorariosTab || !gerarHO) ? null : hoPercentualNumero,
            tipoHonorario: (isHonorariosTab || !gerarHO) ? null : hoTipo,
            honorariosExcecao: !isHonorariosTab && gerarHO && hoExcecao,
            motivoExcecaoHonorarios: (!isHonorariosTab && gerarHO && hoExcecao) ? hoMotivo.trim() : null,
            excecaoCampanha: usaExcecaoCampanha,
            detalheExcecaoCampanha: usaExcecaoCampanha ? detalheExcecaoCampanha : null,
            motivoExcecaoCampanha: usaExcecaoCampanha ? acordoMotivoExcecao.trim() : null
        };

        const performSave = (extraPayload = {}) => {
            const finalPayload = { ...payload, ...extraPayload };
            post("/acordo/save", finalPayload)
                .then((response) => {
                    showSnackbar("Acordo gerado com sucesso! Vá para a aba Acordos para visualizar.", true);
                    setAcordoModalOpen(false);
                    setAcordoMinimizado(false);
                    setSimulacaoOpen(false);
                    setSimulacaoMinimizada(false);
                    setGerarHO(false); // Limpa para evitar loops em negociações sequenciais
                    setHoExcecao(false);
                    setHoMotivo("");
                    fetchContratos(); // Atualiza a lista
                    if (onDataUpdated) onDataUpdated();
                    // O cabeçalho (fichas, status) tem a sua própria leitura: sem o aviso, a ficha de honorários criada pelo
                    // acordo só aparecia no topo depois de recarregar a página
                    window.dispatchEvent(new Event("devedorStatusUpdated"));

                    if (response && response.idHonorarioGerado) {
                        setConfirmAction({
                            open: true,
                            title: "Honorários Gerados",
                            message: "A dívida de Honorários foi criada com sucesso! Você foi redirecionado para a aba de Honorários. Por favor, acesse a nova ficha gerada para formalizar o acordo.",
                            onConfirm: () => {
                                if (changeTab) changeTab(6); // Switch to Honorarios Tab
                            },
                            confirmText: "Ir para Honorários",
                            cancelText: "Fechar"
                        });
                    } else if (acordoFormaPagamento === 'Boleto' && response && response.idPrimeiraParcela) {
                        const canEmitirBoleto = !contratos.some(c => checkContratos.includes(c.id) && c.emiteBoleto === false);
                        if (canEmitirBoleto) {
                            setConfirmAction({
                                open: true,
                                title: "Acordo Gerado",
                                message: "Acordo gerado com sucesso! Deseja emitir o boleto da 1ª parcela agora?",
                                onConfirm: () => {
                                    setLoading(true);
                                    getFile(`/acordo/parcela/boleto/${response.idPrimeiraParcela}`, "boleto.pdf")
                                        .catch(err => showSnackbar(err.message || "Erro ao emitir boleto", false))
                                        .finally(() => setLoading(false));
                                }
                            });
                        }
                    }
                })
                .catch((err) => {
                    showSnackbar(err.message || "Erro ao gerar acordo", false);
                })
                .finally(() => setAcordoLoading(false));
        };

        const isHonorarioSimulation = selectedContratos.length > 0 && selectedContratos.some(id => !contratos.find(c => c.id === id));

        if (contratos.length > 0 && !isHonorariosTab && !isHonorarioSimulation) {
            get(`/acordo/honorarios-state-by-contrato/${contratos[0].id}`)
                .then(state => {
                    if (state && state.hasHonorariosFicha) {
                        if (state.hasActiveAcordo) {
                            setConfirmAction({
                                open: true,
                                title: "Acordo de Honorários Vigente",
                                message: "Este cliente já possui um acordo de honorários em andamento." + (state.hasPaymentsInAcordo ? " ALERTA: Existem pagamentos efetivados neste acordo!" : "") + " Deseja cancelar o acordo de honorários vigente também?",
                                onConfirm: () => performSave({ cancelarAcordoHonorarios: true }),
                                onCancel: () => performSave({ cancelarAcordoHonorarios: false }),
                                confirmText: "Sim, Cancelar",
                                cancelText: "Não Cancelar",
                                confirmColor: "error"
                            });
                        } else {
                            setConfirmAction({
                                open: true,
                                title: "Ficha de Honorários Órfã",
                                message: "Identificamos uma Ficha de Honorários pendente neste contrato, mas sem acordo formalizado. Deseja excluir esta ficha permanentemente?",
                                onConfirm: () => performSave({ excluirFichaHonorarios: true }),
                                onCancel: () => performSave({ excluirFichaHonorarios: false }),
                                confirmText: "Sim, Excluir",
                                cancelText: "Manter Ficha",
                                confirmColor: "error"
                            });
                        }
                    } else {
                        performSave();
                    }
                })
                .catch(err => {
                    performSave();
                });
        } else {
            performSave();
        }
    };

    const [migracaoModalOpen, setMigracaoModalOpen] = useState(false);
    const [tipoSolicitacao, setTipoSolicitacao] = useState("MIGRACAO"); // "MIGRACAO" ou "DEVOLUCAO"
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [carteirasMigracaoOptions, setCarteirasMigracaoOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [statusFichaOptions, setStatusFichaOptions] = useState([]);
    const [selectedCarteiraMigracao, setSelectedCarteiraMigracao] = useState("");
    const [selectedUsuarioMigracao, setSelectedUsuarioMigracao] = useState("");
    const [selectedStatusMigracao, setSelectedStatusMigracao] = useState("");
    const [observacaoMigracao, setObservacaoMigracao] = useState("");
    const [migracaoFile, setMigracaoFile] = useState(null);
    // Dados do Jurídico (código novo, zerar débitos, débito, processo): só na migração para carteira jurídica
    const [pacoteMigracao, setPacoteMigracao] = useState(PACOTE_VAZIO);
    const [dadosCodigoMigracao, setDadosCodigoMigracao] = useState(null);

    useEffect(() => {
        get("/carteira/usuario-contexto").then(res => {
            setCarteirasOptions(res?.carteiras || []);
        }).catch(() => console.error("Erro ao carregar carteiras"));

        get("/carteira/destinos-migracao").then(res => {
            const list = res?.list || res?.data || (Array.isArray(res) ? res : []);
            // Sem a carteira de processos (pelo tipo da carteira, não pelo nome)
            setCarteirasMigracaoOptions(list.filter(ehCarteiraDeCobranca));
        }).catch(() => console.error("Erro ao carregar carteiras de migracao"));
    }, []);

    useEffect(() => {
        if (!selectedCarteiraMigracao) {
            setUsuariosOptions([]);
            return;
        }
        // As pessoas das equipes da carteira de destino (quem pede não está nelas)
        get(`/users/list-all-operators?carteiraId=${selectedCarteiraMigracao}&destinoMigracao=true`).then(res => {
            if (res && res.list) {
                setUsuariosOptions(res.list);
            } else {
                setUsuariosOptions([]);
            }
        }).catch(() => setUsuariosOptions([]));
    }, [selectedCarteiraMigracao]);

    const handleOpenMigracaoModal = () => {
        let targets = selectedContratos;
        if (targets.length === 0) {
            if (contratosFiltrados.length === 1) {
                targets = [contratosFiltrados[0].id];
                setSelectedContratos(targets);
            } else if (contratosFiltrados.length > 1) {
                targets = contratosFiltrados.map(c => c.id);
                setSelectedContratos(targets);
            } else {
                showSnackbar("Não existem contratos disponíveis nesta ficha.", false);
                return;
            }
        }
        setTipoSolicitacao("MIGRACAO");
        setLoading(true);
        Promise.all([
            get("/carteira/destinos-migracao").catch(() => get("/carteira/list-all").catch(() => null)),
            get("/status-ficha/list-all").catch(() => null),
            get(`/devedor/codigo-cliente/${recordId}`).catch(() => null)
        ]).then(([cartRes, statusRes, codigoRes]) => {
            const carteirasRaw = cartRes?.list || cartRes?.data || (Array.isArray(cartRes) ? cartRes : []);
            const filteredCarteiras = carteirasRaw.filter(ehCarteiraDeCobranca);
            const statusList = statusRes?.list || statusRes?.data || (Array.isArray(statusRes) ? statusRes : []);
            setCarteirasMigracaoOptions(filteredCarteiras);
            setStatusFichaOptions(statusList);
            setSelectedCarteiraMigracao(filteredCarteiras.length === 1 ? (filteredCarteiras[0].id || filteredCarteiras[0].cvId) : "");
            setSelectedUsuarioMigracao("");
            setSelectedStatusMigracao("");
            setObservacaoMigracao("");
            setMigracaoFile(null);
            setPacoteMigracao(PACOTE_VAZIO);
            setDadosCodigoMigracao(codigoRes || null);
            setMigracaoModalOpen(true);
        }).finally(() => setLoading(false));
    };

    const carteiraDestinoMigracao = carteirasMigracaoOptions.find(c => String(c.id || c.cvId) === String(selectedCarteiraMigracao));
    const destinoJuridico = tipoSolicitacao === "MIGRACAO" && ehCarteiraJuridica(carteiraDestinoMigracao);

    // Os anexos vão na mesma chamada da devolução/migração (enviarComAnexos): o backend grava a ocorrência e os
    // documentos (aba Documentos da ficha, ligados à ocorrência) juntos; se um arquivo falhar, nada é feito.
    const handleConfirmMigracao = async () => {
        const targets = selectedContratos.length > 0 ? selectedContratos : contratosFiltrados.map(c => c.id);

        if (tipoSolicitacao === "MIGRACAO") {
            if (!selectedCarteiraMigracao) {
                showSnackbar("Selecione uma carteira de destino.", false);
                return;
            }
            setLoading(true);
            try {
                // Direto (quem pode) ou solicitação para a fila; o anexo (tipo Migração de Carteira) vai junto
                const res = await enviarComAnexos("/devedor/testar-migracao", {
                    dados: JSON.stringify({
                        id: recordId,
                        contratosIds: targets,
                        carteiraId: selectedCarteiraMigracao,
                        // Quem pede também sugere responsável e status: o suporte confere na aprovação
                        usuarioId: selectedUsuarioMigracao || null,
                        statusFichaId: selectedStatusMigracao || null,
                        observacao: observacaoMigracao,
                        pacote: destinoJuridico ? pacoteParaEnvio(pacoteMigracao) : null,
                    }),
                }, [migracaoFile]);
                avisarSolicitacoesAlteradas();
                // Migração direta muda carteira, responsável, status e, com os dados do Jurídico, o código do cliente:
                // o cabeçalho e o Cadastro Básico recarregam (senão "Salvar Cadastro" gravaria de volta o código antigo)
                if (res?.direto) window.dispatchEvent(new Event("devedorStatusUpdated"));
                showSnackbar(res?.direto
                    ? "Migração concluída com sucesso!"
                    : "Solicitação de migração de carteira enviada para a Fila de Solicitações.", true);
                setMigracaoModalOpen(false);
                setSelectedContratos([]);
                fetchContratos();
                if (onDataUpdated) onDataUpdated();
            } catch (err) {
                showSnackbar(err.message || "Erro na migração", false);
            } finally {
                setLoading(false);
            }
        } else {
            // DEVOLUCAO
            if (!observacaoMigracao.trim()) {
                showSnackbar("Por favor, preencha o motivo/observação da devolução.", false);
                return;
            }
            setLoading(true);
            const isParcial = selectedContratos.length > 0 && selectedContratos.length < contratosFiltrados.length;
            // Ficha aberta na tela (sem ela, o devedor: o backend escolhe a ficha pelo contexto)
            const fichaDaTela = selectedFicha?.rawId || selectedFicha?.id || idDevedorCarteiraRaw || idDevedorCarteira || recordId;

            try {
                if (isDirectMigrateUser) {
                    // O texto do modal vai na ocorrência de devolução e o anexo fica ligado a ela
                    // (na parcial, vai junto da devolução do primeiro contrato)
                    if (isParcial) {
                        for (let i = 0; i < targets.length; i++) {
                            await enviarComAnexos(`/contratos/devolver/${targets[i]}`, { observacao: observacaoMigracao }, i === 0 ? [migracaoFile] : []);
                        }
                    } else {
                        await enviarComAnexos(`/devedor/devolver/${fichaDaTela}`, { observacao: observacaoMigracao }, [migracaoFile]);
                    }
                    showSnackbar("Devolução realizada com sucesso!", true);
                } else {
                    // Uma ocorrência "solicitado" na ficha para o pedido inteiro, com o anexo ligado a ela
                    if (isParcial) {
                        await enviarComAnexos("/contratos/solicitar-devolucao-lote", { contratosIds: targets, observacao: observacaoMigracao }, [migracaoFile]);
                    } else {
                        await enviarComAnexos(`/devedor/solicitar-devolucao/${fichaDaTela}`, { observacao: observacaoMigracao }, [migracaoFile]);
                    }
                    avisarSolicitacoesAlteradas();
                    showSnackbar("Solicitação de devolução enviada para a Fila de Solicitações.", true);
                }
                setMigracaoModalOpen(false);
                setSelectedContratos([]);
                fetchContratos();
                if (onDataUpdated) onDataUpdated();
            } catch (err) {
                showSnackbar(err.message || "Erro ao processar devolução", false);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleOpenReagruparModal = () => {
        const currentFichaId = selectedFicha?.rawId || selectedFicha?.id || idDevedorCarteiraRaw || idDevedorCarteira;

        const loadFichas = (fichasList) => {
            const outras = (fichasList || []).filter(f => {
                const fId = f.rawId || f.id;
                return String(fId) !== String(currentFichaId);
            });
            setFichasDisponiveis(outras);
            if (outras.length > 0) {
                setReagruparDestinoId(outras[0].rawId || outras[0].id);
            } else {
                setReagruparDestinoId("");
            }
        };

        if (preloadedDevedor?.fichas && preloadedDevedor.fichas.length > 0) {
            loadFichas(preloadedDevedor.fichas);
        } else if (recordId) {
            get(`/devedor/by-id/${recordId}`).then(res => {
                if (res?.fichas) {
                    loadFichas(res.fichas);
                }
            }).catch(() => {});
        }

        if (selectedContratos.length > 0 && selectedContratos.length < contratosFiltrados.length) {
            setReagruparMoverTodos(false);
        } else {
            setReagruparMoverTodos(true);
        }
        setReagruparMotivo("");
        setReagruparModalOpen(true);
    };

    const handleConfirmReagrupar = () => {
        const currentFichaId = selectedFicha?.rawId || selectedFicha?.id || idDevedorCarteiraRaw || idDevedorCarteira;
        if (!currentFichaId) {
            showSnackbar("Não foi possível identificar a ficha de origem atual.", false);
            return;
        }
        if (!reagruparDestinoId) {
            showSnackbar("Selecione a ficha de destino para onde os débitos serão transferidos.", false);
            return;
        }
        if (!reagruparMoverTodos && selectedContratos.length === 0) {
            showSnackbar("Selecione ao menos um contrato na tabela ou escolha 'Mover todos os débitos'.", false);
            return;
        }

        setIsSubmittingReagrupamento(true);
        const payload = {
            idOrigem: currentFichaId,
            idDestino: reagruparDestinoId,
            idsContratos: reagruparMoverTodos ? [] : selectedContratos,
            moverTodos: reagruparMoverTodos,
            motivo: reagruparMotivo
        };

        post("/devedor/carteira/reagrupar-debitos", payload)
            .then(res => {
                showSnackbar(res?.message || "Débitos reagrupados com sucesso!", true);
                setReagruparModalOpen(false);
                setSelectedContratos([]);
                window.dispatchEvent(new Event("devedorStatusUpdated"));
                if (onDataUpdated) onDataUpdated();
                fetchContratos();
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err?.message || "Erro ao reagrupar débitos.";
                showSnackbar(msg, false);
            })
            .finally(() => {
                setIsSubmittingReagrupamento(false);
            });
    };

    const isFilhoFicha = isTerceiroFicha || Boolean(preloadedDevedor?.idDevedorPai);
    const contratosBase = isHonorariosTab
        ? contratos.filter(c => c.isHonorario === true)
        : contratos.filter(c => isFilhoFicha || c.isTerceiro === true || c.isHonorario !== true);
    const isFichaQuitada = selectedFicha?.situacao === 7;
    // Ativo = Aberto ou Baixado Parcial; sem situação também aparece como "Aberto" na tabela
    // Ativo = em cobrança: sem situação, Aberto (1) ou Baixado Parcial (2) e com saldo. Antes valia só a situação, e o
    // contrato Aberto com saldo zero (ex.: migrado assim) aparecia em "Apenas Ativos". Sem o saldo no registro, vale a situação
    const temSaldo = (c) => c.saldo == null || c.saldo === "" || Number(c.saldo) > 0;
    const isContratoAtivo = (c) => (!c.situacao || c.situacao === 1 || c.situacao === 2) && temSaldo(c);
    const temContratosAtivos = contratosBase.some(isContratoAtivo);
    // O botão manda sempre na lista (antes era ignorado em ficha quitada ou sem contratos ativos)
    const contratosFiltrados = apenasAtivos ? contratosBase.filter(isContratoAtivo) : contratosBase;

    // Soma do saldo dos contratos listados (todas as páginas, respeitando "Apenas Ativos") e dos selecionados, em centavos
    const centavosDoSaldo = (c) => Math.round((Number(c?.saldo) || 0) * 100);
    const totalSaldoListado = contratosFiltrados.reduce((soma, c) => soma + centavosDoSaldo(c), 0) / 100;
    const selecionadosListados = contratosFiltrados.filter((c, i) => selectedContratos.includes(c.id || `temp_idx_${i}`));
    const totalSaldoSelecionado = selecionadosListados.reduce((soma, c) => soma + centavosDoSaldo(c), 0) / 100;

    // Contrato em acordo ativo fica suspenso (fora da cobrança) até o acordo ser quitado ou cancelado
    const acordosPorContrato = (situacaoAcordo) => {
        const mapa = new Map();
        acordos.filter(a => a.situacao === situacaoAcordo)
            .forEach(a => (a.contratos || []).forEach(c => { if (c.idContrato) mapa.set(c.idContrato, a); }));
        return mapa;
    };
    const acordoAtivoDoContrato = acordosPorContrato(1);
    const acordoQuitadoDoContrato = acordosPorContrato(3);
    const parcelasDoAcordo = (acordo) => {
        const validas = (acordo.parcelas || []).filter(p => p.situacao !== 4);
        return { pagas: validas.filter(p => p.situacao === 2).length, total: validas.length || acordo.qtdParcelas || 0 };
    };
    // Acordo de só parte dos contratos: o aviso diz quais continuam em cobrança
    const ativosDaFicha = contratosBase.filter(isContratoAtivo);
    const ativosEmAcordo = ativosDaFicha.filter(c => acordoAtivoDoContrato.has(c.id));
    const numerosAcordosAtivos = [...new Set(ativosEmAcordo.map(c => acordoAtivoDoContrato.get(c.id).id))];
    const corAcordo = darkMode ? TEXTO_COLORIDO_ESCURO.info : "info.main";

    // Ao abrir uma ficha sem contrato ativo (ou quitada), o "Apenas Ativos" já vem desligado para a lista não
    // aparecer vazia; depois disso vale o que o usuário escolher no botão
    const fichaComFiltroDefinido = useRef(null);
    const chaveFichaFiltro = `${recordId}|${selectedFicha?.rawId ?? selectedFicha?.id ?? ""}|${isHonorariosTab}`;
    useEffect(() => {
        if (contratosBase.length === 0 || fichaComFiltroDefinido.current === chaveFichaFiltro) return;
        fichaComFiltroDefinido.current = chaveFichaFiltro;
        setApenasAtivos(temContratosAtivos && !isFichaQuitada);
        setPage(0);
    }, [chaveFichaFiltro, contratosBase.length, temContratosAtivos, isFichaQuitada]);

    return (
        <MDBox mt={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox display="flex" alignItems="center">
                    {selectedFicha && (
                        <Chip 
                            icon={<Icon fontSize="small">receipt_long</Icon>}
                            label={`[Ficha #${selectedFicha.rawId || selectedFicha.id}] ${selectedFicha.nomeCarteira || "Carteira"}${selectedFicha.situacao === 1 ? " (Devolvida)" : (selectedFicha.situacao === 7 ? " (Quitada)" : "")}`}
                            color={selectedFicha.situacao === 1 ? "error" : (selectedFicha.situacao === 7 ? "secondary" : "info")}
                            variant="outlined"
                            sx={{ 
                                fontWeight: "bold", 
                                fontSize: "11px", 
                                height: "24px", 
                                mr: 1.5,
                                borderColor: selectedFicha.situacao === 7 ? corRoxo(darkMode) : undefined,
                                color: selectedFicha.situacao === 7 ? corRoxo(darkMode) : undefined
                            }}
                        />
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={apenasAtivos}
                                onChange={(e) => {
                                    setApenasAtivos(e.target.checked);
                                    setPage(0);
                                }}
                                color="info"
                            />
                        }
                        label={<MDTypography variant="button" fontWeight="regular" color="text" sx={{ cursor: "pointer", userSelect: "none", ml: -0.5 }}>Apenas Ativos</MDTypography>}
                        sx={{ margin: 0 }}
                    />
                </MDBox>
                <MDBox display="flex">
                    {recordId && isDirectMigrateUser && (
                        <MDButton 
                            variant="outlined" 
                            color="info" 
                            size="small" 
                            onClick={handleOpenReagruparModal} 
                            sx={{ mr: 1 }}
                        >
                            <Icon sx={{ mr: 0.5 }}>merge</Icon> Reagrupar Débitos
                        </MDButton>
                    )}
                    {/* Só quem migra/devolve direto; quem pede usa "Pedir ao suporte" (abaixo do cabeçalho da ficha) */}
                    {recordId && isDirectMigrateUser && (
                        <MDButton variant="outlined" color="warning" size="small" onClick={handleOpenMigracaoModal} sx={{ mr: 1 }}>
                            <Icon sx={{ mr: 1 }}>swap_horiz</Icon> Migrar / Devolver
                        </MDButton>
                    )}
                    {!isViewOnly && recordId && isAdminOrSuporte && selectedContratos.some(id => contratos.find(c => c.id === id)?.situacao === 4) && (
                        <MDButton variant="contained" color="success" size="small" onClick={handleReativarLote} sx={{ mr: 1 }}>
                            <Icon sx={{ mr: 1 }}>restore</Icon> Reativar Selecionados
                        </MDButton>
                    )}
                    {!isViewOnly && recordId && podeCriarFichaHonorarios && !isTerceiroFicha && (
                        <MDButton variant="outlined" color="info" size="small" onClick={() => setNovaFichaHonorariosAberta(true)} sx={{ mr: 1 }}>
                            <Icon sx={{ mr: 1 }}>gavel</Icon> Nova Ficha de Honorários
                        </MDButton>
                    )}
                    {!isViewOnly && (
                        <MDButton variant="contained" color="info" size="small" onClick={() => handleOpenDialog()}>
                            <Icon sx={{ mr: 1 }}>add</Icon> Novo Contrato
                        </MDButton>
                    )}
                </MDBox>
            </MDBox>

            {ativosEmAcordo.length > 0 && ativosEmAcordo.length < ativosDaFicha.length && (() => {
                const n = ativosEmAcordo.length;
                const restantes = ativosDaFicha.length - n;
                return (
                    <MDBox
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        mb={1.5}
                        px={1.5}
                        py={1}
                        sx={{ borderRadius: 1, border: "1px solid", borderColor: coresDoTema(darkMode).borda, backgroundColor: coresDoTema(darkMode).painel }}
                    >
                        <Icon fontSize="small" sx={{ color: corAcordo, mt: 0.25 }}>handshake</Icon>
                        <MDTypography variant="caption" color="text">
                            <strong>{n} de {ativosDaFicha.length} contratos</strong> em aberto {n === 1 ? "está" : "estão"}{" "}
                            {numerosAcordosAtivos.length > 1 ? "nos acordos" : "no acordo"} nº {numerosAcordosAtivos.join(", ")} e{" "}
                            {n === 1 ? "fica suspenso" : "ficam suspensos"} até a quitação ou o cancelamento do acordo.{" "}
                            {restantes === 1 ? "O outro continua" : `Os outros ${restantes} continuam`} em cobrança.
                        </MDTypography>
                    </MDBox>
                );
            })()}

            <TableContainer sx={{ boxShadow: "none" }}>
                <Table>
                    <MDBox component="thead">
                        <TableRow>
                            <TableCell padding="checkbox" align="center" sx={{ borderBottom: "1px solid #f0f2f5" }}>
                                <Checkbox
                                    color="primary"
                                    indeterminate={selectedContratos.length > 0 && selectedContratos.length < contratosFiltrados.length}
                                    checked={contratosFiltrados.length > 0 && selectedContratos.length === contratosFiltrados.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            <DataTableHeadCell width="auto" sorted={false}>Título</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Carteira</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Descrição</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Parcela</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Vencimento</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Valor</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Saldo</DataTableHeadCell>
                            <DataTableHeadCell width="auto" sorted={false}>Situação</DataTableHeadCell>
                            {mostraColunaAcoes && <DataTableHeadCell width="auto" align="center" sorted={false}>Ações</DataTableHeadCell>}
                        </TableRow>
                    </MDBox>
                    <TableBody>
                        {contratosFiltrados.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={mostraColunaAcoes ? 10 : 9} align="center" sx={{ py: 3, borderBottom: "none" }}>
                                    <MDTypography variant="caption" color="text">
                                        {apenasAtivos && contratosBase.length > 0
                                            ? "Nenhum contrato ativo nesta ficha. Desligue \"Apenas Ativos\" para ver os liquidados, devolvidos e sem saldo."
                                            : "Nenhum contrato encontrado."}
                                    </MDTypography>
                                </TableCell>
                            </TableRow>
                        )}
                        {contratosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                            const rowId = item.id || `temp_idx_${index}`;
                            const isSelected = selectedContratos.includes(rowId);
                            return (
                                <TableRow key={rowId} selected={isSelected} hover>
                                    <TableCell padding="checkbox" align="center" sx={{ borderBottom: "1px solid #f0f2f5" }}>
                                        <Checkbox
                                            color="primary"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                setSelectedContratos(prev =>
                                                    prev.includes(rowId) ? prev.filter(i => i !== rowId) : [...prev, rowId]
                                                );
                                            }}
                                        />
                                    </TableCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDBox display="flex" alignItems="center">
                                            <MDTypography variant="caption" fontWeight="regular" color="text">{item.titulo}</MDTypography>
                                            {item.numeroProcesso && (
                                                <Chip label={`Processo: ${item.numeroProcesso}`} size="small" color="info" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                                            )}
                                        </MDBox>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        {(() => {
                                            const matchingFicha = (preloadedDevedor?.fichas || []).find(f => f.id === item.idDevedorCarteira || String(f.rawId) === String(item.idDevedorCarteira));
                                            const fichaIdText = matchingFicha?.rawId ? `[#${matchingFicha.rawId}] ` : "";
                                            const cartText = item.nomeCarteira || matchingFicha?.nomeCarteira;
                                            return cartText ? (
                                                <MDTypography variant="caption" fontWeight="medium" color="info">
                                                    {fichaIdText}{cartText}
                                                </MDTypography>
                                            ) : (
                                                <MDTypography variant="caption" fontWeight="regular" color="text">-</MDTypography>
                                            );
                                        })()}
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="regular"
                                            color="text"
                                            title={item.descricao}
                                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200, display: 'block' }}
                                        >
                                            {item.descricao || "-"}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="regular" color="text">{item.parcela}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="regular" color="text">{item.vencimento ? moment.utc(item.vencimento).format("DD/MM/YYYY") : ""}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="regular" color="text">{toCurrency(item.valorOriginal)}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="regular" color="text">{toCurrency(item.saldo)}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        {(() => {
                                            const acordoAtivo = acordoAtivoDoContrato.get(item.id);
                                            if (acordoAtivo && isContratoAtivo(item)) {
                                                const { pagas, total } = parcelasDoAcordo(acordoAtivo);
                                                const data = acordoAtivo.dataFormalizacao ? ` de ${moment(acordoAtivo.dataFormalizacao).format("DD/MM/YYYY")}` : "";
                                                return (
                                                    <Tooltip title={`Contrato no acordo nº ${acordoAtivo.id}${data}: fica suspenso, fora da cobrança, até o acordo ser quitado ou cancelado. ${pagas} de ${total} parcelas pagas.${changeTab ? " Clique para ver o acordo." : ""}`}>
                                                        <MDBox>
                                                            <Chip
                                                                icon={<Icon sx={{ fontSize: "14px !important" }}>handshake</Icon>}
                                                                label={`Em acordo nº ${acordoAtivo.id}`}
                                                                size="small"
                                                                color="info"
                                                                variant="outlined"
                                                                onClick={changeTab ? () => changeTab(4) : undefined}
                                                                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600 }}
                                                            />
                                                            <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.65rem", lineHeight: 1.3, mt: 0.25 }}>
                                                                Suspenso até quitar ou cancelar
                                                                <br />
                                                                {pagas} de {total} parcelas pagas
                                                            </MDTypography>
                                                        </MDBox>
                                                    </Tooltip>
                                                );
                                            }
                                            const acordoQuitado = acordoQuitadoDoContrato.get(item.id);
                                            return (
                                                <>
                                                    <MDTypography variant="caption" fontWeight="regular" color="text">{getSituacaoLabel(item.situacao)}</MDTypography>
                                                    {acordoQuitado && item.situacao === 3 && (
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.65rem" }}>
                                                            pelo acordo nº {acordoQuitado.id}
                                                        </MDTypography>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </DataTableBodyCell>
                                    {mostraColunaAcoes && (
                                        <DataTableBodyCell align="center" isProcess={true}>
                                            {!isViewOnly && !isFila && (
                                                <>
                                                    <IconButton color="info" size="small" onClick={() => handleOpenDialog(item)} sx={{ p: 0.5 }}>
                                                        <Icon fontSize="small">edit</Icon>
                                                    </IconButton>
                                                    <IconButton color="error" size="small" onClick={() => handleDeleteClick(item)} sx={{ p: 0.5, ml: 1 }}>
                                                        <Icon fontSize="small">delete</Icon>
                                                    </IconButton>
                                                </>
                                            )}
                                            {/* Pedir devolução: pelo "Pedir ao suporte" da ficha; aqui só a devolução direta */}
                                            {recordId && item.situacao !== 4 && isAdminOrSuporte && (
                                                <IconButton color="error" size="small" onClick={() => handleDevolverDireto(item)} sx={{ p: 0.5, ml: 1 }} title="Devolver Contrato" disabled={isFichaDevolvida}>
                                                    <Icon fontSize="small">assignment_return</Icon>
                                                </IconButton>
                                            )}
                                            {recordId && item.situacao === 4 && isAdminOrSuporte && (
                                                <IconButton color="success" size="small" onClick={() => handleReativarContratoDireto(item)} sx={{ p: 0.5, ml: 1 }} title="Reativar Contrato">
                                                    <Icon fontSize="small">restore</Icon>
                                                </IconButton>
                                            )}
                                        </DataTableBodyCell>
                                    )}
                                </TableRow>
                            );
                        })}
                        {contratosFiltrados.length > 0 && (
                            <TableRow sx={{ backgroundColor: coresDoTema(darkMode).painel }}>
                                <TableCell colSpan={7} align="right" sx={{ py: 1, borderBottom: "none" }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text">
                                        Saldo total ({contratosFiltrados.length}{" "}
                                        {contratosFiltrados.length === 1
                                            ? (apenasAtivos ? "contrato ativo" : "contrato")
                                            : (apenasAtivos ? "contratos ativos" : "contratos")})
                                    </MDTypography>
                                    {selecionadosListados.length > 0 && selecionadosListados.length < contratosFiltrados.length && (
                                        <MDTypography variant="caption" color="text" display="block">
                                            Selecionados ({selecionadosListados.length})
                                        </MDTypography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 1, borderBottom: "none" }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ whiteSpace: "nowrap" }}>
                                        {toCurrency(totalSaldoListado)}
                                    </MDTypography>
                                    {selecionadosListados.length > 0 && selecionadosListados.length < contratosFiltrados.length && (
                                        <MDTypography variant="caption" color="text" display="block" sx={{ whiteSpace: "nowrap" }}>
                                            {toCurrency(totalSaldoSelecionado)}
                                        </MDTypography>
                                    )}
                                </TableCell>
                                <TableCell colSpan={mostraColunaAcoes ? 2 : 1} sx={{ py: 1, borderBottom: "none" }} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {contratosFiltrados.length > 0 && (
                <TablePagination
                    component="div"
                    count={contratosFiltrados.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Itens por pág:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                        color: darkMode ? "#fff" : "inherit",
                        ".MuiTablePagination-toolbar": { minHeight: 36, paddingLeft: 1, paddingRight: 1 },
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0, color: darkMode ? "#fff" : "inherit" },
                        ".MuiTablePagination-select": { fontSize: "0.75rem", color: darkMode ? "#fff" : "inherit" },
                        ".MuiTablePagination-selectIcon": { color: darkMode ? "#fff" : "inherit" },
                        ".MuiTablePagination-actions button": { color: darkMode ? "#fff" : "inherit" }
                    }}
                />
            )}

            <MDBox mt={3} p={2} bgcolor="background.default" borderRadius={2} border="1px solid" borderColor="divider">
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4} md={3}>
                        <MDInput
                            type="date"
                            label="Data Base para Simulação"
                            name="dataBaseSimulacao"
                            value={dataBaseSimulacao}
                            onChange={(e) => setDataBaseSimulacao(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4}>
                        <MDButton
                            variant="contained"
                            color="success"
                            size="medium"
                            fullWidth
                            onClick={() => handleSimularCalculoDevedor()}
                            disabled={simulacaoLoading || contratos.length === 0}
                        >
                            <Icon sx={{ mr: 1 }}>calculate</Icon> Simular Ficha Completa
                        </MDButton>
                    </Grid>
                </Grid>
            </MDBox>

            <NovaFichaHonorariosDialog
                open={novaFichaHonorariosAberta}
                onClose={() => setNovaFichaHonorariosAberta(false)}
                recordId={recordId}
                carteiraInicialNome={selectedFicha?.nomeCarteira}
                onSalvo={(res) => {
                    setNovaFichaHonorariosAberta(false);
                    showSnackbar(res?.isTerceiro
                        ? `Ficha de honorários #${res?.numeroFicha} do terceiro cadastrada.`
                        : `Ficha de honorários #${res?.numeroFicha} cadastrada.`, true);
                    if (onFichaHonorariosCriada) onFichaHonorariosCriada(res);
                    else if (onDataUpdated) onDataUpdated();
                    // O topo da ficha só relê as fichas com este aviso
                    window.dispatchEvent(new Event("devedorStatusUpdated"));
                }}
            />

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{selectedContrato ? "Editar Contrato" : "Novo Contrato"}</DialogTitle>
                <Formik
                    initialValues={{
                        id: selectedContrato?.id || "",
                        titulo: selectedContrato?.titulo || "",
                        descricao: selectedContrato?.descricao || "",
                        parcela: selectedContrato?.parcela || "Única",
                        // Mês/ano de referência (UMC): só aparecem no título que já tem
                        mesReferencia: selectedContrato?.mesReferencia || "",
                        anoReferencia: selectedContrato?.anoReferencia || "",
                        temReferencia: Boolean(selectedContrato?.mesReferencia || selectedContrato?.anoReferencia),
                        vencimento: selectedContrato?.vencimento ? moment.utc(selectedContrato.vencimento).format("YYYY-MM-DD") : "",
                        dataInclusao: (selectedContrato?.dataInclusao || selectedContrato?.cdCriacao)
                            ? moment(selectedContrato.dataInclusao || selectedContrato.cdCriacao).format("DD/MM/YYYY HH:mm")
                            : (selectedContrato ? "-" : aberturaDialog),
                        valorOriginal: selectedContrato?.valorOriginal ? formatCurrencyInput(selectedContrato.valorOriginal) : "",
                        saldo: selectedContrato?.saldo ? formatCurrencyInput(selectedContrato.saldo) : "",
                        porConta: selectedContrato?.porConta ? formatCurrencyInput(selectedContrato.porConta) : "",
                        situacao: selectedContrato?.situacao || 1,
                        marcacoesIds: selectedContrato?.marcacoesIds || [],
                        // Obrigatória (é ela que cria a ficha do devedor novo): a do contrato; senão a da ficha aberta (pelo
                        // nome: o id da ficha vem criptografado); senão a única das equipes do usuário (já vem travada)
                        carteiraId: selectedContrato?.idCarteira
                            || (selectedFicha?.nomeCarteira ? carteirasOptions.find(c => c.nome === selectedFicha.nomeCarteira)?.id : null)
                            || (carteirasOptions.length === 1 ? carteirasOptions[0].id : ""),
                    }}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, handleChange, setFieldValue, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid container spacing={2} alignItems="flex-start">
                                    <Grid item xs={12} sm={2.8}>
                                        <FormField
                                            type="text"
                                            label="Título"
                                            name="titulo"
                                            value={values.titulo}
                                            onChange={handleChange}
                                            placeholder="Ex: Nota Fiscal 123"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <FormField
                                            type="text"
                                            label="Parcela"
                                            name="parcela"
                                            value={values.parcela}
                                            onChange={handleChange}
                                            placeholder="Ex: 01/12"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2.4}>
                                        <FormField
                                            type="date"
                                            label="Vencimento"
                                            name="vencimento"
                                            value={values.vencimento}
                                            onChange={handleChange}
                                            required
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2.4}>
                                        <FormField
                                            type="text"
                                            label="Data de Inclusão"
                                            name="dataInclusao"
                                            value={values.dataInclusao}
                                            InputProps={{ readOnly: true }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2.4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                            Situação
                                        </InputLabel>
                                        <MDInput
                                            select
                                            name="situacao"
                                            value={values.situacao}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value={1}>Aberto</MenuItem>
                                            <MenuItem value={2}>Baixado Parcial</MenuItem>
                                            <MenuItem value={3}>Liquidado</MenuItem>
                                            <MenuItem value={4}>Cancelado</MenuItem>
                                            <MenuItem value={6} disabled>Migrado (zerado na migração para o Jurídico)</MenuItem>
                                        </MDInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                            Marcação Especial
                                        </InputLabel>
                                        <SeletorMarcacoes
                                            carteira={values.carteiraId}
                                            alvos={[2, 3]}
                                            name="marcacoesIds"
                                            value={values.marcacoesIds}
                                            onChange={(ids) => setFieldValue("marcacoesIds", ids)}
                                            isDisabled={isViewOnly}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                            Carteira *
                                        </InputLabel>
                                        <MDInput
                                            select
                                            name="carteiraId"
                                            value={values.carteiraId || ""}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                            disabled={carteirasOptions.length === 1 || isViewOnly}
                                        >
                                            {carteirasOptions.map(c => (
                                                <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                                            ))}
                                        </MDInput>
                                    </Grid>
                                    {values.temReferencia && (
                                        <>
                                            <Grid item xs={6} sm={2}>
                                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Mês de referência</InputLabel>
                                                <MDInput name="mesReferencia" type="number" value={values.mesReferencia} onChange={handleChange}
                                                    size="small" fullWidth inputProps={{ min: 1, max: 12 }} InputProps={{ readOnly: isViewOnly }} />
                                            </Grid>
                                            <Grid item xs={6} sm={2}>
                                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Ano de referência</InputLabel>
                                                <MDInput name="anoReferencia" type="number" value={values.anoReferencia} onChange={handleChange}
                                                    size="small" fullWidth inputProps={{ min: 1900, max: 2100 }} InputProps={{ readOnly: isViewOnly }} />
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <MDTypography variant="caption" color="text" display="block" px={1} pt={3}>
                                                    A mensalidade a que o título se refere (UMC): vai na Prestação de Contas e no arquivo de retorno.
                                                </MDTypography>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <FormField
                                            type="text"
                                            label="Descrição do Débito"
                                            name="descricao"
                                            value={values.descricao}
                                            onChange={handleChange}
                                            placeholder="Ex: Confissão de dívida assinada..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            type="text"
                                            label="Valor Original"
                                            name="valorOriginal"
                                            value={values.valorOriginal}
                                            onChange={(e) => {
                                                const formatted = formatCurrencyInput(e.target.value);
                                                setFieldValue("valorOriginal", formatted);
                                                // Saldo = valor original - o pago por conta (como no TMK)
                                                const saldo = Math.max(arredondarCentavos((formatUncurrency(formatted) || 0) - (formatUncurrency(values.porConta) || 0)), 0);
                                                setFieldValue("saldo", formatted ? formatCurrencyInput(saldo) : "");
                                            }}
                                            placeholder="R$ 0,00"
                                            required
                                        />
                                    </Grid>
                                    {(() => {
                                        // Valor pago, "por conta" (no TMK, PorConta): as baixas avulsas somam nele sozinhas; à mão se lança
                                        // o que o devedor pagou fora do sistema, e o saldo cai (ou volta) pela diferença. Não é
                                        // recebimento. No contrato em acordo ativo, o pago entra pelas parcelas
                                        const emAcordo = selectedContrato?.id ? acordoAtivoDoContrato.get(selectedContrato.id) : null;
                                        const pagoBaixas = Number(selectedContrato?.pagoBaixas) || 0;
                                        return (
                                            <Grid item xs={12} sm={4}>
                                                <FormField
                                                    type="text"
                                                    label="Valor pago (por conta)"
                                                    name="porConta"
                                                    value={values.porConta}
                                                    disabled={Boolean(emAcordo) || isViewOnly}
                                                    onChange={(e) => {
                                                        const formatted = formatCurrencyInput(e.target.value);
                                                        const antes = formatUncurrency(values.porConta) || 0;
                                                        const depois = formatUncurrency(formatted) || 0;
                                                        const saldo = Math.max(arredondarCentavos((formatUncurrency(values.saldo) || 0) - (depois - antes)), 0);
                                                        setFieldValue("porConta", formatted);
                                                        if (values.valorOriginal) {
                                                            setFieldValue("saldo", formatCurrencyInput(saldo));
                                                            if (saldo === 0) setFieldValue("situacao", 3);
                                                            else if ([1, 3].includes(Number(values.situacao))) setFieldValue("situacao", depois > 0 ? 2 : 1);
                                                        }
                                                    }}
                                                    placeholder="R$ 0,00"
                                                />
                                                <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5, lineHeight: 1.3 }}>
                                                    {emAcordo
                                                        ? `Contrato no acordo nº ${emAcordo.id}: o pagamento entra pelas parcelas do acordo.`
                                                        : "O principal já pago: as baixas avulsas e as parcelas de acordo somam aqui sozinhas. Mudar à mão (pagamento feito fora do sistema) abate ou devolve a diferença no saldo; não conta como recebimento."}
                                                    {pagoBaixas > 0 ? ` Baixas avulsas no sistema: ${toCurrency(pagoBaixas)}.` : ""}
                                                </MDTypography>
                                            </Grid>
                                        );
                                    })()}
                                    {(() => {
                                        // Dois cliques no Saldo (pedido da Aline em 23/10/2026: baixa errada e estorno deixaram o saldo acima
                                        // do valor): refaz a conta valor original - valor pago (por conta) - descontos das baixas avulsas;
                                        // sem pagamento, o saldo volta a ser o valor original. Só preenche o campo: grava no Salvar
                                        const desconto = Number(selectedContrato?.descontoBaixas) || 0;
                                        const recalcular = () => {
                                            if (isViewOnly || !values.valorOriginal) return;
                                            const pago = formatUncurrency(values.porConta) || 0;
                                            const saldo = Math.max(arredondarCentavos((formatUncurrency(values.valorOriginal) || 0) - pago - desconto), 0);
                                            setFieldValue("saldo", formatCurrencyInput(saldo));
                                            // A situação acompanha (o devolvido fica devolvido), como ao mudar o valor pago
                                            if (Number(values.situacao) !== 4) {
                                                setFieldValue("situacao", saldo === 0 ? 3 : pago > 0 || desconto > 0 ? 2 : 1);
                                            }
                                        };
                                        return (
                                            <Grid item xs={12} sm={4}>
                                                <Tooltip title={isViewOnly ? "" : "Dois cliques: recalcula o saldo (valor original - valor pago - descontos das baixas)"}>
                                                    <MDBox onDoubleClick={recalcular}>
                                                        <FormField
                                                            type="text"
                                                            label="Saldo"
                                                            name="saldo"
                                                            value={values.saldo}
                                                            onChange={(e) => setFieldValue("saldo", formatCurrencyInput(e.target.value))}
                                                            placeholder="R$ 0,00"
                                                            required
                                                        />
                                                    </MDBox>
                                                </Tooltip>
                                                {!isViewOnly && (
                                                    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: -0.5, lineHeight: 1.3 }}>
                                                        Dois cliques recalculam: valor original - valor pago{desconto > 0 ? ` - ${toCurrency(desconto)} de desconto das baixas` : ""}.
                                                    </MDTypography>
                                                )}
                                            </Grid>
                                        );
                                    })()}
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <MDButton color="secondary" onClick={handleCloseDialog}>
                                    Cancelar
                                </MDButton>
                                <MDButton color="info" type="submit" disabled={isSubmitting}>
                                    Salvar
                                </MDButton>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* Simulação Dialog */}
            {simulacaoData && (() => {
                const handlePrintSimulacao = () => {
                    const printWindow = window.open('', '_blank', 'width=900,height=700');
                    if (!printWindow) return;

                    const devedorNome = simulacaoData.devedorNome || "";
                    const dataSimulacao = simulacaoData.dataSimulacao ? `Simulação gerada para a data base: ${simulacaoData.dataSimulacao}` : `Atraso: ${simulacaoData.atrasoDias || 0} dias`;

                    let contratosRows = "";
                    if (simulacaoData.contratosCalculadosList && simulacaoData.contratosCalculadosList.length > 0) {
                        contratosRows = simulacaoData.contratosCalculadosList.map(c => `
                            <tr>
                                <td style="padding: 8px 10px; border: 1px solid #ccc;">${c.titulo || ''} ${c.parcela && c.parcela !== "Única" ? `(${c.parcela})` : ''}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right;">${c.atrasoDias || 0} dias</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right;">${toCurrency(c.valorOriginal)}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right; color: #ef6c00;">${toCurrencyComSinal(c.valorCorrecao)}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right; color: #d32f2f;">+${toCurrency(c.valorMulta)}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right; color: #d32f2f;">+${toCurrency(c.valorJuros)}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right; color: #d32f2f;">+${toCurrency(c.valorHonorarios)}</td>
                                <td style="padding: 8px 10px; border: 1px solid #ccc; text-align: right; font-weight: bold;">${toCurrency(c.totalAtualizado)}</td>
                            </tr>
                        `).join('');
                    }

                    const htmlContent = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                            <meta charset="utf-8" />
                            <title>Cálculo Devedor - ${devedorNome}</title>
                            <style>
                                @page { margin: 15mm; }
                                body {
                                    font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
                                    color: #222;
                                    margin: 0;
                                    padding: 20px;
                                    background: #fff;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }
                                .header {
                                    margin-bottom: 20px;
                                    border-bottom: 2px solid #b8860b;
                                    padding-bottom: 10px;
                                }
                                .devedor-nome {
                                    font-size: 20px;
                                    font-weight: bold;
                                    margin: 0 0 6px 0;
                                    color: #111;
                                }
                                .data-simulacao {
                                    font-size: 13px;
                                    color: #555;
                                    margin: 0;
                                }
                                .section-title {
                                    font-size: 14px;
                                    font-weight: bold;
                                    margin-top: 20px;
                                    margin-bottom: 10px;
                                    color: #333;
                                }
                                table {
                                    width: 100%;
                                    border-collapse: collapse;
                                    font-size: 13px;
                                    margin-bottom: 20px;
                                }
                                th {
                                    background-color: #b8860b;
                                    color: #ffffff;
                                    padding: 9px 10px;
                                    font-size: 11px;
                                    text-transform: uppercase;
                                    text-align: left;
                                    font-weight: bold;
                                    border: 1px solid #000;
                                }
                                th.right { text-align: right; }
                                td { border: 1px solid #ccc; }
                                .total-box {
                                    background-color: #f0f2f5;
                                    border: 1px solid #000;
                                    border-radius: 4px;
                                    padding: 12px 15px;
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    font-size: 16px;
                                    font-weight: bold;
                                    margin-top: 20px;
                                }
                            </style>
                        </head>
                        <body>
                            <div class="header">
                                <div class="devedor-nome">${devedorNome}</div>
                                <div class="data-simulacao">${dataSimulacao}</div>
                            </div>

                            ${contratosRows ? `
                                <div class="section-title">Detalhamento por Contrato</div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Contrato / Parc.</th>
                                            <th class="right">Atraso</th>
                                            <th class="right">Original</th>
                                            <th class="right">Correção</th>
                                            <th class="right">Multa</th>
                                            <th class="right">Juros</th>
                                            <th class="right">Honorários</th>
                                            <th class="right">Atualizado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${contratosRows}
                                    </tbody>
                                    <tfoot>
                                        <tr style="font-weight: bold; background: #f0f2f5;">
                                            <td>Total</td>
                                            <td class="right">-</td>
                                            <td class="right">${toCurrency(simulacaoData.valorOriginal)}</td>
                                            <td class="right">${toCurrencyComSinal(simulacaoData.valorCorrecao)}</td>
                                            <td class="right">+${toCurrency(simulacaoData.valorMulta)}</td>
                                            <td class="right">+${toCurrency(simulacaoData.valorJuros)}</td>
                                            <td class="right">+${toCurrency(simulacaoData.valorHonorarios)}</td>
                                            <td class="right">${toCurrency(simulacaoData.totalAtualizado)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            ` : ''}

                            ${contratosRows ? '' : `
                            <div class="total-box">
                                <span>Total Atualizado</span>
                                <span>${toCurrency(simulacaoData.totalAtualizado)}</span>
                            </div>
                            `}
                        </body>
                        </html>
                    `;

                    printWindow.document.write(htmlContent);
                    printWindow.document.close();
                };

                return (
                    <Dialog
                        open={simulacaoOpen && !simulacaoMinimizada}
                        onClose={() => { setSimulacaoOpen(false); setSimulacaoMinimizada(false); }}
                        maxWidth="lg"
                        fullWidth
                        PaperProps={{
                            sx: {
                                maxWidth: "1280px",
                                width: "100%",
                                borderRadius: "12px"
                            }
                        }}
                    >
                        <DialogTitle sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <MDTypography variant="h6">Simulação de Campanha</MDTypography>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                                <IconButton size="small" onClick={handleMinimizarSimulacao} title="Minimizar">
                                    <Icon fontSize="small">remove</Icon>
                                </IconButton>
                                <IconButton size="small" onClick={() => { setSimulacaoOpen(false); setSimulacaoMinimizada(false); removeWindow(`simulacao-${recordId || "current"}`); }} title="Fechar">
                                    <Icon fontSize="small">close</Icon>
                                </IconButton>
                            </MDBox>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 2 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} mb={1}>
                                    {simulacaoData.devedorNome && (
                                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"} fontWeight="medium">{simulacaoData.devedorNome}</MDTypography>
                                    )}
                                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <MDTypography variant="button" color="info" fontWeight="medium">Campanha Aplicada: {simulacaoData.campanhaNome}</MDTypography>
                                        {simulacaoData.criterioEnquadramento && (
                                            <MDBox
                                                component="span"
                                                sx={{
                                                    px: 1,
                                                    py: 0.2,
                                                    borderRadius: "4px",
                                                    bgcolor: darkMode ? "rgba(76, 175, 80, 0.2)" : "#e8f5e9",
                                                    border: darkMode ? "1px solid rgba(76, 175, 80, 0.4)" : "1px solid #c8e6c9",
                                                    color: darkMode ? "#81c784" : "#2e7d32",
                                                    fontSize: "11px",
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                Critério: {simulacaoData.criterioEnquadramento}
                                            </MDBox>
                                        )}
                                    </MDBox>
                                    {simulacaoData.dataSimulacao ? (
                                        <MDTypography variant="caption" display="block" color="text">Simulação gerada para a data base: {simulacaoData.dataSimulacao}</MDTypography>
                                    ) : (
                                        <MDTypography variant="caption" display="block" color="text">Atraso: {simulacaoData.atrasoDias} dias</MDTypography>
                                    )}
                                </Grid>

                                {simulacaoData.contratosCalculadosList && simulacaoData.contratosCalculadosList.length > 0 ? (
                                    <Grid item xs={12} mb={1}>
                                        <MDTypography variant="caption" fontWeight="medium" mb={1} display="block">Detalhamento por Contrato</MDTypography>
                                        <TableContainer sx={{ border: "1px solid #f0f2f5", borderRadius: 2 }}>
                                            <Table size="small">
                                                <MDBox component="thead">
                                                    <TableRow>
                                                        <DataTableHeadCell width="auto" sorted={false} sx={{ whiteSpace: "nowrap" }}>Contrato / Parc.</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Atraso</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Original</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Correção</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Multa</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Juros</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Honorários</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false} sx={{ whiteSpace: "nowrap" }}>Atualizado</DataTableHeadCell>
                                                    </TableRow>
                                                </MDBox>
                                                <TableBody>
                                                    {simulacaoData.contratosCalculadosList.map((c, idx) => (
                                                        <TableRow key={idx}>
                                                            <DataTableBodyCell isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>{c.titulo} {c.parcela && c.parcela !== "Única" ? `(${c.parcela})` : ''}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>{c.atrasoDias} dias</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>{toCurrency(c.valorOriginal)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#ffb74d" : "#ef6c00" }}>{toCurrencyComSinal(c.valorCorrecao)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(c.valorMulta)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(c.valorJuros)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(c.valorHonorarios)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>{toCurrency(c.totalAtualizado)}</MDTypography>
                                                            </DataTableBodyCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                                <MDBox component="tfoot">
                                                    <TableRow sx={{ backgroundColor: darkMode ? "#344767" : "#f0f2f5", borderTop: darkMode ? "2px solid #5a6a85" : "2px solid #e0e0e0" }}>
                                                        <DataTableBodyCell isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>Total</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" color="text">-</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>{toCurrency(simulacaoData.valorOriginal)}</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffb74d" : "#ef6c00" }}>{toCurrencyComSinal(simulacaoData.valorCorrecao)}</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorMulta)}</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorJuros)}</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorHonorarios)}</MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell align="right" isProcess={true} sx={{ whiteSpace: "nowrap" }}>
                                                            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>{toCurrency(simulacaoData.totalAtualizado)}</MDTypography>
                                                        </DataTableBodyCell>
                                                    </TableRow>
                                                </MDBox>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={6} md>
                                            <MDTypography variant="caption" color="text">Valor Original</MDTypography>
                                            <MDTypography variant="caption" display="block" fontWeight="bold" color={darkMode ? "white" : "dark"}>{toCurrency(simulacaoData.valorOriginal)}</MDTypography>
                                        </Grid>
                                        <Grid item xs={6} md>
                                            <MDTypography variant="caption" color="text">Correção Monetária</MDTypography>
                                            <MDTypography variant="caption" display="block" fontWeight="bold" sx={{ color: darkMode ? "#ffb74d" : "#ef6c00" }}>{toCurrencyComSinal(simulacaoData.valorCorrecao)}</MDTypography>
                                        </Grid>
                                        <Grid item xs={6} md>
                                            <MDTypography variant="caption" color="text">Multa ({simulacaoData.multaPercentual}%)</MDTypography>
                                            <MDTypography variant="caption" display="block" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorMulta)}</MDTypography>
                                        </Grid>
                                        <Grid item xs={6} md>
                                            <MDTypography variant="caption" color="text">Juros ({simulacaoData.jurosMensalPercentual}% a.m.)</MDTypography>
                                            <MDTypography variant="caption" display="block" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorJuros)}</MDTypography>
                                        </Grid>
                                        <Grid item xs={6} md>
                                            <MDTypography variant="caption" color="text">Honorários ({simulacaoData.honorariosPercentual}%)</MDTypography>
                                            <MDTypography variant="caption" display="block" fontWeight="bold" sx={{ color: darkMode ? "#ff7961" : "#d32f2f" }}>+{toCurrency(simulacaoData.valorHonorarios)}</MDTypography>
                                        </Grid>
                                    </>
                                )}

                                {!simulacaoData.contratosCalculadosList || simulacaoData.contratosCalculadosList.length === 0 ? (
                                    <Grid item xs={12}>
                                        <MDBox p={1} bgcolor={darkMode ? "#344767" : "#f0f2f5"} borderRadius={1} mt={1} display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>Total Atualizado</MDTypography>
                                            <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>{toCurrency(simulacaoData.totalAtualizado)}</MDTypography>
                                        </MDBox>
                                    </Grid>
                                ) : null}

                                {simulacaoData.opcoesParcelamento && simulacaoData.opcoesParcelamento.length > 0 && (
                                    <Grid item xs={12} mt={2}>
                                        <MDTypography variant="caption" fontWeight="medium" mb={1} display="block">Opções de Parcelamento</MDTypography>
                                        <TableContainer sx={{ boxShadow: "none" }}>
                                            <Table size="small">
                                                <MDBox component="thead">
                                                    <TableRow>
                                                        <DataTableHeadCell width="auto" sorted={false}>Meio Pagto</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Parcelas</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Entrada (%)</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Desc. Principal</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Desc. Juros</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Desc. Multa</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" sorted={false}>Desc. Honorários</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="right" sorted={false}>Total a Pagar</DataTableHeadCell>
                                                        <DataTableHeadCell width="auto" align="center" sorted={false}>Ação</DataTableHeadCell>
                                                    </TableRow>
                                                </MDBox>
                                                <TableBody>
                                                    {simulacaoData.opcoesParcelamento.map((p, i) => (
                                                        <TableRow key={i}>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" fontWeight="medium" color={p.formaPagamentoDescricao && p.formaPagamentoDescricao !== "Todas" ? "info" : "text"}>
                                                                    {p.formaPagamentoDescricao || "Todas"}
                                                                </MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" color="text">{p.parcelaMinima} a {p.parcelaMaxima}x</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" fontWeight="bold" color="info">
                                                                    {p.percentualMinimoEntrada || p.percentualEntrada || 0}%
                                                                </MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" color="text">{p.descontoPrincipal}%</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" color="text">{p.descontoJuros}%</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" color="text">{p.descontoMulta}%</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell isProcess={true}>
                                                                <MDTypography variant="caption" color="text">{p.descontoHonorarios}%</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="right" isProcess={true}>
                                                                <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>{toCurrency(p.totalComDesconto)}</MDTypography>
                                                            </DataTableBodyCell>
                                                            <DataTableBodyCell align="center" isProcess={true}>
                                                                <MDButton size="small" color="info" variant="outlined" onClick={() => handleOpenAcordoModal(p)}>
                                                                    Gerar
                                                                </MDButton>
                                                            </DataTableBodyCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}

                                <Grid item xs={12} mt={2} display="flex" justifyContent="center">
                                    <MDButton
                                        size="small"
                                        color="info"
                                        variant={darkMode ? "contained" : "outlined"}
                                        onClick={handleOpenAcordoManual}
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Nova Simulação Manual
                                    </MDButton>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 1 }}>
                            <MDButton
                                size="small"
                                color="info"
                                variant="contained"
                                onClick={handlePrintSimulacao}
                                startIcon={<Icon>print</Icon>}
                                sx={{ mr: 1 }}
                            >
                                Imprimir Cálculo
                            </MDButton>
                            <MDButton
                                size="small"
                                color={darkMode ? "white" : "secondary"}
                                onClick={() => { setSimulacaoOpen(false); setSimulacaoMinimizada(false); removeWindow(`simulacao-${recordId || "current"}`); }}
                            >
                                Fechar
                            </MDButton>
                        </DialogActions>
                    </Dialog>
                );
            })()}

            {/* Gerar Acordo Dialog */}
            <Dialog
                open={acordoModalOpen && !acordoMinimizado}
                onClose={() => { setAcordoModalOpen(false); setAcordoMinimizado(false); }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                        backgroundImage: "none",
                        borderRadius: "12px",
                        overflow: "hidden"
                    }
                }}
            >
                <DialogTitle sx={{ p: 2.5, pb: 1.5, borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <MDBox display="flex" alignItems="center" gap={1.5}>
                            <MDBox
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "10px",
                                    backgroundColor: darkMode ? "rgba(25, 118, 210, 0.25)" : "#e3f2fd",
                                    color: darkMode ? "#64b5f6" : "#1976d2",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <Icon fontSize="medium">calendar_month</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    Gerar Acordo / Cronograma
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                    Configure as condições abaixo para gerar o acordo.
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <MDBox display="flex" alignItems="center" gap={1}>
                            {simulacaoData?.campanhaNome && (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    px={1.5}
                                    py={0.8}
                                    borderRadius="md"
                                    sx={{
                                        backgroundColor: darkMode ? "rgba(33, 150, 243, 0.15)" : "#e3f2fd",
                                        border: darkMode ? "1px solid rgba(33, 150, 243, 0.3)" : "1px solid #bbdefb"
                                    }}
                                >
                                    <Icon fontSize="small" sx={{ color: darkMode ? "#64b5f6" : "#1976d2" }}>info</Icon>
                                    <MDTypography variant="caption" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.75rem" }}>
                                        Os limites e percentuais seguem as regras da campanha: <b>{simulacaoData.campanhaNome}</b>
                                        {simulacaoData.criterioEnquadramento && (
                                            <> · Enquadramento: <b>{simulacaoData.criterioEnquadramento}</b></>
                                        )}
                                    </MDTypography>
                                </MDBox>
                            )}

                            <MDBox display="flex" alignItems="center" gap={0.5} ml={1}>
                                <IconButton size="small" onClick={handleMinimizarAcordo} title="Minimizar">
                                    <Icon fontSize="small">remove</Icon>
                                </IconButton>
                                <IconButton size="small" onClick={() => { setAcordoModalOpen(false); setAcordoMinimizado(false); removeWindow(`acordo-${recordId || "current"}`); }} title="Fechar">
                                    <Icon fontSize="small">close</Icon>
                                </IconButton>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2.5, backgroundColor: darkMode ? ESCURO.painel : "#fafafa" }}>
                    {acordoOpcaoSelecionada && (() => {
                        const isAvista = Number(acordoQtdParcelas || 1) <= 1 && !acordoComEntrada;
                        const isCobextraCarteira = String(idDevedorCarteira) === "1" ||
                            (formRef?.current?.values?.idCarteira && String(formRef.current.values.idCarteira) === "1") ||
                            (formRef?.current?.values?.carteiraId && String(formRef.current.values.carteiraId) === "1") ||
                            contratos.some(c => String(c.idCarteira) === "1" || String(c.idDevedorCarteira) === "1" || String(c.carteiraId) === "1");

                        const descPrincipal = Number(regraCampanhaAtual?.descontoPrincipal || 0);
                        const descJuros = Number(regraCampanhaAtual?.descontoJuros || 0);
                        const descMulta = Number(regraCampanhaAtual?.descontoMulta || 0);
                        const descHonorarios = Number(regraCampanhaAtual?.descontoHonorarios || 0);
                        const hasHonorarios = descHonorarios > 0;
                        const allEqual = descPrincipal === descJuros && descPrincipal === descMulta && (!hasHonorarios || descPrincipal === descHonorarios);
                        const componentesIguais = ["Principal", "Juros", "Multa", hasHonorarios ? "Honorários" : null].filter(Boolean).join(" + ");

                        const opcoes = simulacaoData?.opcoesParcelamento || [];
                        const regraAvista = opcoes.find(r => r.parcelaMinima === 1 && r.parcelaMaxima === 1) || opcoes[0];
                        const regrasParceladas = opcoes.filter(r => r.parcelaMaxima > 1);
                        const descAvistaPct = regraAvista ? regraAvista.descontoPrincipal : descPrincipal;
                        const descParceladoPct = regrasParceladas.length > 0 ? Math.max(...regrasParceladas.map(r => r.descontoPrincipal)) : descPrincipal;

                        const totalAtualizado = valorIntegralCampanha || 0;
                        const valDigitado = valorDigitadoAcordo || 0;
                        let pctAplicado = 0;
                        if (totalAtualizado > 0) {
                            if (Math.abs(valDigitado - valorMinimoCampanha) < 0.05) {
                                pctAplicado = descPrincipal;
                            } else if (valDigitado >= totalAtualizado) {
                                pctAplicado = 0;
                            } else {
                                const ratio = (totalAtualizado - valDigitado) / (totalAtualizado - (valorMinimoCampanha || 1));
                                pctAplicado = Math.round(Math.max(0, Math.min(descPrincipal, ratio * descPrincipal)));
                            }
                        }

                        return (
                            <Grid container spacing={2}>
                                {/* Linha 1 - Coluna 1: Banner da Campanha */}
                                {simulacaoData && (
                                    <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                        <MDBox
                                            p={2}
                                            borderRadius="lg"
                                            width="100%"
                                            display="flex"
                                            flexDirection="column"
                                            justifyContent="space-between"
                                            sx={{
                                                backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                                                border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0",
                                                boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)"
                                            }}
                                        >
                                            <MDBox>
                                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                    <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "1rem" }}>
                                                        Campanha: {simulacaoData.campanhaNome || "Padrão"}
                                                    </MDTypography>
                                                    {simulacaoData.criterioEnquadramento && (
                                                        <MDBox
                                                            component="span"
                                                            sx={{
                                                                px: 1,
                                                                py: 0.2,
                                                                borderRadius: "4px",
                                                                bgcolor: darkMode ? "rgba(76, 175, 80, 0.2)" : "#e8f5e9",
                                                                border: darkMode ? "1px solid rgba(76, 175, 80, 0.4)" : "1px solid #c8e6c9",
                                                                color: darkMode ? "#81c784" : "#2e7d32",
                                                                fontSize: "11px",
                                                                fontWeight: "bold"
                                                            }}
                                                        >
                                                            Critério: {simulacaoData.criterioEnquadramento}
                                                        </MDBox>
                                                    )}
                                                </MDBox>
                                                {regraCampanhaAtual ? (
                                                    <>
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.8rem", mt: 0.3 }}>
                                                            Faixa: <b>{totalParcelasConfiguradas}x</b> ({regraCampanhaAtual.parcelaMinima} a {regraCampanhaAtual.parcelaMaxima} parcelas) · {regraCampanhaAtual.formaPagamentoDescricao || "Todas"}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" color="info" display="block" fontWeight="medium" sx={{ fontSize: "0.8rem", mt: 0.4 }}>
                                                            {allEqual ? (
                                                                <>Desconto máximo: <b>{descPrincipal}%</b> · {componentesIguais}</>
                                                            ) : (
                                                                <>Descontos máximos: Principal <b>{descPrincipal}%</b> · Juros <b>{descJuros}%</b> · Multa <b>{descMulta}%</b>{hasHonorarios ? <> · Honorários <b>{descHonorarios}%</b></> : null}</>
                                                            )}
                                                        </MDTypography>
                                                    </>
                                                ) : (
                                                    <MDTypography variant="caption" color={acordoIsExcecao ? "success" : "error"} display="block" fontWeight="bold" sx={{ mt: 0.5 }}>
                                                        {acordoIsExcecao
                                                            ? `✓ Parcelamento em ${totalParcelasConfiguradas}x para ${acordoFormaPagamento} liberado como Exceção.`
                                                            : `⚠️ A campanha não prevê parcelamento em ${totalParcelasConfiguradas}x para ${acordoFormaPagamento}${maxParcelasCampanhaForma > 0 ? ` (máximo permitido: ${maxParcelasCampanhaForma}x)` : ""}.`
                                                        }
                                                    </MDTypography>
                                                )}
                                            </MDBox>

                                            {regraCampanhaAtual && (
                                                <MDBox mt={1.5}>
                                                    {allEqual ? (
                                                        <MDBox
                                                            px={2}
                                                            py={1}
                                                            borderRadius="md"
                                                            textAlign="center"
                                                            sx={{
                                                                backgroundColor: darkMode ? "rgba(0, 230, 118, 0.15)" : "#e8f5e9",
                                                                border: darkMode ? "1px solid rgba(0, 230, 118, 0.3)" : "1px solid #c8e6c9"
                                                            }}
                                                        >
                                                            <MDTypography variant="h5" fontWeight="bold" sx={{ color: darkMode ? "#00e676" : "#2e7d32", lineHeight: 1.1 }}>
                                                                {descPrincipal}%
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text" sx={{ fontSize: "0.72rem", fontWeight: "medium" }}>
                                                                Desconto Geral ({componentesIguais})
                                                            </MDTypography>
                                                        </MDBox>
                                                    ) : (
                                                        <MDBox display="flex" gap={1} alignItems="center">
                                                            <MDBox textAlign="center" px={1} py={0.8} borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f1f5f9", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0", flex: 1 }}>
                                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.95rem" }}>{descPrincipal}%</MDTypography>
                                                                <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.68rem" }}>Principal</MDTypography>
                                                            </MDBox>
                                                            <MDBox textAlign="center" px={1} py={0.8} borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f1f5f9", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0", flex: 1 }}>
                                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.95rem" }}>{descJuros}%</MDTypography>
                                                                <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.68rem" }}>Juros</MDTypography>
                                                            </MDBox>
                                                            <MDBox textAlign="center" px={1} py={0.8} borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f1f5f9", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0", flex: 1 }}>
                                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.95rem" }}>{descMulta}%</MDTypography>
                                                                <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.68rem" }}>Multa</MDTypography>
                                                            </MDBox>
                                                            {hasHonorarios && (
                                                                <MDBox textAlign="center" px={1} py={0.8} borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f1f5f9", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0", flex: 1 }}>
                                                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.95rem" }}>{descHonorarios}%</MDTypography>
                                                                    <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.68rem" }}>Honorários</MDTypography>
                                                                </MDBox>
                                                            )}
                                                        </MDBox>
                                                    )}
                                                </MDBox>
                                            )}

                                            <MDBox mt={2} pt={1} sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e2e8f0" }}>
                                                <MDTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                                                    Regra configurada para {totalParcelasConfiguradas} parcela(s).
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </Grid>
                                )}

                                {/* Linha 1 - Coluna 2: Faixa de Negociação (ao lado de Campanha) */}
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <MDBox
                                        p={2}
                                        borderRadius="lg"
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{
                                            height: "100%",
                                            backgroundColor: darkMode ? ESCURO.cartao : "#f2f9f6",
                                            border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #c8e6c9",
                                            boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)"
                                        }}
                                    >
                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                                                <MDBox
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: "50%",
                                                        backgroundColor: darkMode ? "rgba(0, 230, 118, 0.2)" : "#e8f5e9",
                                                        color: darkMode ? "#00e676" : "#2e7d32",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: "bold",
                                                        fontSize: "0.85rem"
                                                    }}
                                                >
                                                    $
                                                </MDBox>
                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.9rem" }}>
                                                    Faixa de negociação (valor total)
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                                                    border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #d7ecd9"
                                                }}
                                            >
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6} textAlign="center" sx={{ borderRight: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0f0e3" }}>
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem" }}>Valor mínimo</MDTypography>
                                                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: darkMode ? "#00e676" : "#2e7d32", fontSize: "1.1rem" }}>
                                                            {toCurrency(valorMinimoCampanha)}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#81c784" : "#388e3c", fontSize: "0.7rem", fontWeight: "medium" }}>
                                                            ({descPrincipal}% de desconto)
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={6} textAlign="center">
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem" }}>Valor máximo</MDTypography>
                                                        <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "1.1rem" }}>
                                                            {toCurrency(valorIntegralCampanha)}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                                            (sem desconto)
                                                        </MDTypography>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </MDBox>

                                        <MDBox mt="auto" pt={1.5} sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #d7ecd9" }}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem", fontWeight: "medium" }}>À vista</MDTypography>
                                                    <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold" sx={{ fontSize: "0.8rem" }}>
                                                        {descAvistaPct}% de desconto
                                                    </MDTypography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.75rem", fontWeight: "medium" }}>Parcelado</MDTypography>
                                                    <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold" sx={{ fontSize: "0.8rem" }}>
                                                        {descParceladoPct}% de desconto
                                                    </MDTypography>
                                                </Grid>
                                            </Grid>
                                        </MDBox>
                                    </MDBox>
                                </Grid>

                                {/* Linha 2 - Coluna 1: Etapa 1 - Valor Negociado */}
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <MDBox
                                        p={2}
                                        borderRadius="lg"
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{
                                            height: "100%",
                                            backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                                            border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0",
                                            boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)"
                                        }}
                                    >
                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1.2} mb={2}>
                                                <MDBox
                                                    sx={{
                                                        width: 26,
                                                        height: 26,
                                                        borderRadius: "50%",
                                                        backgroundColor: "#1976d2",
                                                        color: "#ffffff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    1
                                                </MDBox>
                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                    Valor negociado
                                                </MDTypography>
                                                <MDBox
                                                    px={1.2}
                                                    py={0.3}
                                                    borderRadius="md"
                                                    sx={{
                                                        backgroundColor: darkMode ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd",
                                                        border: darkMode ? "1px solid #64b5f6" : "1px solid #90caf9"
                                                    }}
                                                >
                                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#90caf9" : "#1565c0", fontSize: "0.75rem" }}>
                                                        {pctAplicado}% desc.
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>

                                            {/* Slider Interativo */}
                                            {acordoOpcaoSelecionada.isManual && valorIntegralCampanha > 0 && (
                                                <MDBox px={1} mb={2}>
                                                    <Slider
                                                        min={valorMinimoCampanha || 0}
                                                        max={Math.max(valorIntegralCampanha, (valorMinimoCampanha || 0) + 1)}
                                                        step={1}
                                                        value={Math.max(valorMinimoCampanha || 0, Math.min(valorIntegralCampanha || 0, valDigitado))}
                                                        onChange={(e, val) => setAcordoValorManual(formatCurrencyInput(val))}
                                                        sx={{
                                                            color: "#00e676",
                                                            height: 6,
                                                            "& .MuiSlider-track": {
                                                                background: "linear-gradient(90deg, #00e676 0%, #29b6f6 100%)",
                                                                border: "none"
                                                            },
                                                            "& .MuiSlider-rail": {
                                                                backgroundColor: darkMode ? ESCURO.hover : "#e2e8f0",
                                                                opacity: 1
                                                            },
                                                            "& .MuiSlider-thumb": {
                                                                height: 20,
                                                                width: 20,
                                                                backgroundColor: "#ffffff",
                                                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                                                border: "2px solid #29b6f6",
                                                                "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                                                                    boxShadow: "0 0 0 8px rgba(41, 182, 246, 0.2)"
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <MDBox display="flex" justifyContent="space-between" mt={-0.5}>
                                                        <Tooltip title="Clique para aplicar o Valor Mínimo com Desconto Máximo">
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{
                                                                    cursor: "pointer",
                                                                    color: darkMode ? "#81c784" : "#2e7d32",
                                                                    fontWeight: "bold",
                                                                    fontSize: "0.75rem"
                                                                }}
                                                                onClick={() => setAcordoValorManual(formatCurrencyInput(valorMinimoCampanha))}
                                                            >
                                                                {toCurrency(valorMinimoCampanha)}
                                                            </MDTypography>
                                                        </Tooltip>
                                                        <Tooltip title="Clique para aplicar o Valor Integral Sem Desconto">
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{
                                                                    cursor: "pointer",
                                                                    color: "text",
                                                                    fontWeight: "bold",
                                                                    fontSize: "0.75rem"
                                                                }}
                                                                onClick={() => {
                                                                    setAcordoValorManual(formatCurrencyInput(valorIntegralCampanha));
                                                                    invalidateCronograma();
                                                                }}
                                                            >
                                                                {toCurrency(valorIntegralCampanha)}
                                                            </MDTypography>
                                                        </Tooltip>
                                                    </MDBox>
                                                </MDBox>
                                            )}

                                            {/* Campo de Entrada de Valor posicionado abaixo da barra */}
                                            <MDBox
                                                p={1.5}
                                                borderRadius="md"
                                                sx={{
                                                    backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa",
                                                    border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0"
                                                }}
                                            >
                                                <Grid container spacing={1.5} alignItems="center" justifyContent="space-between">
                                                    <Grid item xs={12} sm={6.5}>
                                                        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.9rem" }}>
                                                            Entre com o valor negociado:
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={5.5} textAlign="right">
                                                        {acordoOpcaoSelecionada.isManual ? (
                                                            <MDInput
                                                                type="text"
                                                                value={acordoValorManual}
                                                                onChange={(e) => {
                                                                    setAcordoValorManual(formatCurrencyInput(e.target.value));
                                                                    invalidateCronograma();
                                                                }}
                                                                error={isValorAbaixoCampanha}
                                                                placeholder="R$ 0,00"
                                                                fullWidth
                                                                sx={{ "& .MuiInputBase-input": {
                                                                        fontSize: "1.1rem",
                                                                        fontWeight: "bold",
                                                                        textAlign: "right",
                                                                        color: isValorAbaixoCampanha
                                                                            ? (darkMode ? "#ff5252 !important" : "#d32f2f !important")
                                                                            : (darkMode ? "#ffffff !important" : "#1a2035 !important")
                                                                    }, "& .MuiInputBase-root": {
                                                                        height: "40px",
                                                                        minHeight: "40px",
                                                                        backgroundColor: isValorAbaixoCampanha
                                                                            ? (darkMode ? "rgba(244, 67, 54, 0.18)" : "#fff5f5")
                                                                            : (darkMode ? ESCURO.cartao : "#ffffff"),
                                                                        border: isValorAbaixoCampanha
                                                                            ? "1px solid #f44336 !important"
                                                                            : (darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #d2d6da"),
                                                                        borderRadius: "8px",
                                                                        boxShadow: isValorAbaixoCampanha ? "0 0 6px rgba(244, 67, 54, 0.35)" : "none"
                                                                    } }}
                                                            />
                                                        ) : (
                                                            <MDTypography variant="h6" fontWeight="bold" color="info">
                                                                {toCurrency(acordoOpcaoSelecionada.totalComDesconto)}
                                                            </MDTypography>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </MDBox>

                                        <MDBox mt={2}>
                                            {isValorAbaixoCampanha && (
                                                <MDTypography variant="caption" color="error" fontWeight="bold" display="block">
                                                    ⚠️ Valor abaixo do permitido pela campanha (Mínimo: {toCurrency(valorMinimoCampanha)}). Para valores menores, ative &quot;Liberar como Exceção&quot;.
                                                </MDTypography>
                                            )}
                                            {valorAbaixoComExcecao && (
                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#81c784" : "#2e7d32", fontWeight: "bold", display: "block" }}>
                                                    ✓ Valor abaixo do mínimo da campanha liberado como Exceção.
                                                </MDTypography>
                                            )}
                                            {isParcelasForaCampanha && (
                                                <MDTypography variant="caption" color="error" fontWeight="bold" display="block">
                                                    ⚠️ Parcelamento em {totalParcelasConfiguradas}x não permitido para {acordoFormaPagamento} pela campanha{maxParcelasCampanhaForma > 0 ? ` (máximo permitido: ${maxParcelasCampanhaForma}x)` : ""}. Para prosseguir, altere a forma de pagamento ou ative &quot;Liberar como Exceção&quot;.
                                                </MDTypography>
                                            )}
                                            {acordoIsExcecao && !regraCampanhaAtual && (
                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#81c784" : "#2e7d32", fontWeight: "bold", display: "block" }}>
                                                    ✓ Parcelamento em {totalParcelasConfiguradas}x para {acordoFormaPagamento} liberado como Exceção da Campanha.
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Grid>

                                {/* Linha 2 - Coluna 2: Etapa 2 - Condições do Acordo */}
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <MDBox
                                        p={2}
                                        borderRadius="lg"
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{
                                            height: "100%",
                                            backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                                            border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0",
                                            boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)"
                                        }}
                                    >
                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1.2} mb={1.5}>
                                                <MDBox
                                                    sx={{
                                                        width: 26,
                                                        height: 26,
                                                        borderRadius: "50%",
                                                        backgroundColor: "#1976d2",
                                                        color: "#ffffff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    2
                                                </MDBox>
                                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                    Condições do acordo
                                                </MDTypography>
                                            </MDBox>

                                            <Grid container spacing={1.5} alignItems="center">
                                                {/* Modalidade: À vista vs Parcelado */}
                                                <Grid item xs={12}>
                                                    <InputLabel sx={{ px: 0.5, pb: 0.3, pt: 0 }}>Forma de Pagamento</InputLabel>
                                                    <MDBox display="flex" gap={1} height="38px" alignItems="center">
                                                        <Box
                                                            onClick={() => {
                                                                setAcordoQtdParcelas(1);
                                                                setAcordoComEntrada(false);
                                                                setAcordoPeriodicidade(30);
                                                                invalidateCronograma();
                                                            }}
                                                            sx={{
                                                                flex: 1,
                                                                height: "38px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 0.8,
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                border: "1px solid",
                                                                borderColor: isAvista ? "#1976d2" : (darkMode ? ESCURO.divisoria : "#cfd8dc"),
                                                                backgroundColor: isAvista ? (darkMode ? "rgba(25, 118, 210, 0.2)" : "#e3f2fd") : "rgba(0, 0, 0, 0)",
                                                                transition: "all 150ms ease-in-out"
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: "50%",
                                                                    border: `2px solid ${isAvista ? "#1976d2" : "#9e9e9e"}`,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center"
                                                                }}
                                                            >
                                                                {isAvista && <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#1976d2" }} />}
                                                            </Box>
                                                            <MDTypography variant="button" fontWeight={isAvista ? "bold" : "regular"} color={isAvista ? "info" : "text"} sx={{ fontSize: "0.8rem" }}>
                                                                À vista
                                                            </MDTypography>
                                                        </Box>

                                                        <Box
                                                            onClick={() => {
                                                                if (Number(acordoQtdParcelas) <= 1) {
                                                                    setAcordoQtdParcelas(2);
                                                                }
                                                                invalidateCronograma();
                                                            }}
                                                            sx={{
                                                                flex: 1,
                                                                height: "38px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 0.8,
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                border: "1px solid",
                                                                borderColor: !isAvista ? "#1976d2" : (darkMode ? ESCURO.divisoria : "#cfd8dc"),
                                                                backgroundColor: !isAvista ? (darkMode ? "rgba(25, 118, 210, 0.2)" : "#e3f2fd") : "rgba(0, 0, 0, 0)",
                                                                transition: "all 150ms ease-in-out"
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: "50%",
                                                                    border: `2px solid ${!isAvista ? "#1976d2" : "#9e9e9e"}`,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center"
                                                                }}
                                                            >
                                                                {!isAvista && <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#1976d2" }} />}
                                                            </Box>
                                                            <MDTypography variant="button" fontWeight={!isAvista ? "bold" : "regular"} color={!isAvista ? "info" : "text"} sx={{ fontSize: "0.8rem" }}>
                                                                Parcelado
                                                            </MDTypography>
                                                        </Box>
                                                    </MDBox>
                                                </Grid>

                                                {/* Periodicidade */}
                                                <Grid item xs={6}>
                                                    <InputLabel sx={{ px: 0.5, pb: 0.3, pt: 0 }}>Periodicidade (dias)</InputLabel>
                                                    <MDInput
                                                        type="number"
                                                        value={acordoPeriodicidade}
                                                        onChange={(e) => {
                                                            setAcordoPeriodicidade(e.target.value);
                                                            invalidateCronograma();
                                                        }}
                                                        fullWidth

                                                    />
                                                    <MDTypography variant="caption" color="text" display="block" sx={{ px: 0.5, pt: 0.3 }}>
                                                        30 = todo mês no mesmo dia; domingo vai para a segunda.
                                                    </MDTypography>
                                                </Grid>

                                                {/* Qtd. Parcelas */}
                                                <Grid item xs={6}>
                                                    <InputLabel sx={{ px: 0.5, pb: 0.3, pt: 0 }}>Qtd. Parcelas</InputLabel>
                                                    <MDInput
                                                        type="number"
                                                        value={acordoQtdParcelas}
                                                        disabled={isAvista}
                                                        onChange={(e) => {
                                                            const rawVal = parseInt(e.target.value, 10);
                                                            if (isNaN(rawVal)) {
                                                                setAcordoQtdParcelas("");
                                                                invalidateCronograma();
                                                                return;
                                                            }
                                                            const maxVal = acordoIsExcecao ? 999 : (maxParcelasCampanhaForma > 0 ? maxParcelasCampanhaForma : 999);
                                                            const newQtd = Math.max(1, Math.min(maxVal, rawVal));
                                                            setAcordoQtdParcelas(newQtd);
                                                            invalidateCronograma();
                                                        }}
                                                        inputProps={{ min: 1, max: acordoIsExcecao ? 999 : (maxParcelasCampanhaForma > 0 ? maxParcelasCampanhaForma : 999) }}
                                                        error={isParcelasForaCampanha}
                                                        fullWidth
                                                       
                                                    />
                                                </Grid>

                                                {/* Primeiro Vencimento */}
                                                <Grid item xs={6}>
                                                    <InputLabel sx={{ px: 0.5, pb: 0.3, pt: 0 }}>Primeiro Vencimento</InputLabel>
                                                    <MDInput
                                                        type="date"
                                                        value={acordoPrimeiroVencimento}
                                                        onChange={(e) => handleChangePrimeiroVencimento(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        fullWidth
                                                       
                                                    />
                                                </Grid>

                                                {/* Meio de Pagamento */}
                                                <Grid item xs={6}>
                                                    <InputLabel sx={{ px: 0.5, pb: 0.3, pt: 0 }}>Meio de Liquidação</InputLabel>
                                                    <MDInput
                                                        select
                                                        value={acordoFormaPagamento}
                                                        onChange={(e) => {
                                                            const novaForma = e.target.value;
                                                            setAcordoFormaPagamento(novaForma);
                                                            invalidateCronograma();
                                                            if (!acordoIsExcecao) {
                                                                const regrasNova = (simulacaoData?.opcoesParcelamento || []).filter(r => 
                                                                    !r.formaPagamentoDescricao || r.formaPagamentoDescricao === "Todas" || r.formaPagamentoDescricao === novaForma
                                                                );
                                                                const maxNova = regrasNova.length > 0 
                                                                    ? Math.max(...regrasNova.map(r => Number(r.parcelaMaxima) || 1))
                                                                    : 0;
                                                                if (maxNova > 0 && Number(acordoQtdParcelas) > maxNova) {
                                                                    setAcordoQtdParcelas(maxNova);
                                                                }
                                                            }
                                                        }}
                                                        fullWidth
                                                       
                                                    >
                                                        <MenuItem value="Boleto">Boleto</MenuItem>
                                                        <MenuItem value="Pix">Pix</MenuItem>
                                                        <MenuItem value="Depósito">Depósito/Transferência</MenuItem>
                                                        <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
                                                        <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                                                        <MenuItem value="Pagto no Credor">Pagto no Credor</MenuItem>
                                                    </MDInput>
                                                </Grid>

                                                {/* Switches no layout padrão do sistema */}
                                                <Grid item xs={12} sm={!isAvista && !isCobextraCarteira ? 6 : 12} display="flex" alignItems="center" pt={1}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={acordoIsExcecao}
                                                                onChange={(e) => {
                                                                    setAcordoIsExcecao(e.target.checked);
                                                                    invalidateCronograma();
                                                                }}
                                                                color="info"
                                                            />
                                                        }
                                                        label={
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="medium"
                                                                color={darkMode ? "white" : "dark"}
                                                                sx={{ fontSize: "0.85rem", cursor: "pointer", userSelect: "none" }}
                                                            >
                                                                Liberar como Exceção da Campanha
                                                            </MDTypography>
                                                        }
                                                        sx={{ margin: 0 }}
                                                    />
                                                </Grid>

                                                {!isAvista && !isCobextraCarteira && (
                                                    <Grid item xs={12} sm={acordoOpcaoSelecionada.isManual ? 6 : 12} display="flex" alignItems="center" pt={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={acordoComEntrada}
                                                                    onChange={(e) => {
                                                                        setAcordoComEntrada(e.target.checked);
                                                                        invalidateCronograma();
                                                                    }}
                                                                    color="info"
                                                                />
                                                            }
                                                            label={
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="medium"
                                                                    color={darkMode ? "white" : "dark"}
                                                                    sx={{ fontSize: "0.85rem", cursor: "pointer", userSelect: "none" }}
                                                                >
                                                                    Possui Pagamento de Entrada?
                                                                </MDTypography>
                                                            }
                                                            sx={{ margin: 0 }}
                                                        />
                                                    </Grid>
                                                )}

                                                {acordoIsExcecao && (
                                                    <Grid item xs={12}>
                                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                                            Motivo da exceção{usaExcecaoCampanha ? " *" : ""}
                                                        </InputLabel>
                                                        <MDInput
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            value={acordoMotivoExcecao}
                                                            onChange={(e) => setAcordoMotivoExcecao(e.target.value)}
                                                            placeholder="Por que este acordo sai fora das regras da campanha"
                                                            inputProps={{ maxLength: 500 }}
                                                        />
                                                        <MDTypography variant="caption" color="text" display="block" px={1}>
                                                            {usaExcecaoCampanha
                                                                ? `Fora da campanha: ${detalheExcecaoCampanha}. O motivo vai na ocorrência do acordo.`
                                                                : "Obrigatório só se o acordo ficar fora das regras da campanha (valor abaixo do mínimo ou parcelas fora das faixas). Vai na ocorrência do acordo."}
                                                        </MDTypography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </MDBox>

                                        <MDBox>

                                            {/* Seção Parcelado com Entrada */}
                                            {!isAvista && acordoComEntrada && !isCobextraCarteira && (
                                                <MDBox mt={1.5} p={1.5} borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0" }}>
                                                    <Grid container spacing={1.5}>
                                                        <Grid item xs={12} sm={parseInt(acordoQtdEntrada) > 1 ? 4 : 4}>
                                                            <MDInput type="text" label="Valor Entrada" value={acordoValorEntrada} onChange={(e) => handleMainValorEntradaChange(e.target.value)} fullWidth size="small" />
                                                        </Grid>
                                                        <Grid item xs={12} sm={parseInt(acordoQtdEntrada) > 1 ? 4 : 4}>
                                                            <MDInput type="number" label="Qtd Parc. Entrada" value={acordoQtdEntrada} onChange={(e) => {
                                                                setAcordoQtdEntrada(Math.max(1, parseInt(e.target.value) || 1));
                                                                invalidateCronograma();
                                                            }} fullWidth size="small" />
                                                        </Grid>
                                                        <Grid item xs={12} sm={parseInt(acordoQtdEntrada) > 1 ? 4 : 4}>
                                                            <MDInput type="date" label="Venc. Entrada" value={acordoVencimentoEntrada} onChange={(e) => {
                                                                setAcordoVencimentoEntrada(e.target.value);
                                                                invalidateCronograma();
                                                            }} InputLabelProps={{ shrink: true }} fullWidth size="small" />
                                                        </Grid>
                                                        {parseInt(acordoQtdEntrada) === 1 && (
                                                            <Grid item xs={12} display="flex" alignItems="center">
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={acordoMleList[0] || false}
                                                                            onChange={(e) => {
                                                                                setAcordoMleList([e.target.checked]);
                                                                                invalidateCronograma();
                                                                            }}
                                                                            color="info"
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label={<MDTypography variant="caption" color="text">MLE</MDTypography>}
                                                                />
                                                            </Grid>
                                                        )}

                                                        {parseInt(acordoQtdEntrada) > 1 && acordoEntradasDetalhadas.length > 0 && (
                                                            <Grid item xs={12}>
                                                                <MDTypography variant="caption" fontWeight="medium" display="block" mb={1} sx={{ opacity: 0.8 }}>
                                                                    Detalhamento das Parcelas de Entrada:
                                                                </MDTypography>
                                                                <Grid container spacing={1} sx={{ pl: 1, borderLeft: "3px solid #1A73E8", my: 0.5 }}>
                                                                    {acordoEntradasDetalhadas.map((ent, idx) => (
                                                                        <React.Fragment key={idx}>
                                                                            <Grid item xs={12} sm={3} display="flex" alignItems="center">
                                                                                <MDTypography variant="button" fontWeight="medium" size="small">
                                                                                    Entrada {idx + 1}
                                                                                </MDTypography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={3}>
                                                                                <MDInput
                                                                                    type="text"
                                                                                    label="Valor"
                                                                                    value={ent.valor}
                                                                                    onChange={(e) => {
                                                                                        let newList = [...acordoEntradasDetalhadas];
                                                                                        newList[idx].valor = formatCurrencyInput(e.target.value);
                                                                                        setAcordoEntradasDetalhadas(newList);
                                                                                        let sum = 0;
                                                                                        for (let item of newList) {
                                                                                            sum += (formatUncurrency(item.valor) || 0);
                                                                                        }
                                                                                        setAcordoValorEntrada(formatCurrencyInput(sum));
                                                                                        invalidateCronograma();
                                                                                    }}
                                                                                    fullWidth
                                                                                    size="small"
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={3}>
                                                                                <MDInput
                                                                                    type="date"
                                                                                    label="Vencimento"
                                                                                    value={ent.vencimento}
                                                                                    onChange={(e) => {
                                                                                        let newList = [...acordoEntradasDetalhadas];
                                                                                        newList[idx].vencimento = e.target.value;
                                                                                        setAcordoEntradasDetalhadas(newList);
                                                                                        invalidateCronograma();
                                                                                    }}
                                                                                    InputLabelProps={{ shrink: true }}
                                                                                    fullWidth
                                                                                    size="small"
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={3} display="flex" alignItems="center">
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Switch
                                                                                            checked={acordoMleList[idx] || false}
                                                                                            onChange={(e) => {
                                                                                                const newMle = [...acordoMleList];
                                                                                                newMle[idx] = e.target.checked;
                                                                                                setAcordoMleList(newMle);
                                                                                                invalidateCronograma();
                                                                                            }}
                                                                                            color="info"
                                                                                            size="small"
                                                                                        />
                                                                                    }
                                                                                    label={<MDTypography variant="caption" color="text">MLE</MDTypography>}
                                                                                />
                                                                            </Grid>
                                                                        </React.Fragment>
                                                                    ))}
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Grid>

                                {/* Linha 3: Cronograma de Pagamento (Exibido apenas quando for parcelar) */}
                                {!isAvista && (
                                    <Grid item xs={12}>
                                        <MDBox
                                            p={2}
                                            borderRadius="lg"
                                            sx={{
                                                backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                                                border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0",
                                                boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)"
                                            }}
                                        >
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <Icon color="info" fontSize="medium">calendar_today</Icon>
                                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.95rem" }}>
                                                        Cronograma de Pagamento ({totalParcelasConfiguradas} parcelas)
                                                    </MDTypography>
                                                    {resimulandoVencimento && (
                                                        <MDTypography variant="caption" color="text">
                                                            Atualizando os valores para a nova data de vencimento...
                                                        </MDTypography>
                                                    )}
                                                </MDBox>
                                                <MDBox display="flex" gap={1}>
                                                    <MDButton
                                                        variant="contained"
                                                        color="info"
                                                        size="small"
                                                        onClick={handleCalcularCronograma}
                                                        disabled={isBloqueadoPorRegra || resimulandoVencimento}
                                                        startIcon={<Icon>calculate</Icon>}
                                                    >
                                                        Calcular Parcelas
                                                    </MDButton>
                                                    {acordoParcelasGeradas.length > 0 && (
                                                        <MDButton
                                                            variant="outlined"
                                                            color="info"
                                                            size="small"
                                                            startIcon={<Icon>print</Icon>}
                                                            onClick={() => {
                                                                setExportDataPrimeiraParcela(acordoParcelasGeradas[0]?.vencimento || moment().format("YYYY-MM-DD"));
                                                                setOpenExportCronogramaModal(true);
                                                            }}
                                                        >
                                                            Exportar Modelo
                                                        </MDButton>
                                                    )}
                                                </MDBox>
                                            </MDBox>

                                            {acordoParcelasGeradas.length > 0 ? (
                                                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none", maxHeight: 280, border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0" }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead sx={{ display: "table-header-group" }}>
                                                            <TableRow>
                                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Nº</MDTypography></TableCell>
                                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Tipo</MDTypography></TableCell>
                                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Vencimento</MDTypography></TableCell>
                                                                <TableCell><MDTypography variant="caption" color="text" fontWeight="medium">Valor</MDTypography></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {acordoParcelasGeradas.map((p, pIdx) => (
                                                                <TableRow key={pIdx}>
                                                                    <TableCell><MDTypography variant="caption">{p.numeroParcela}</MDTypography></TableCell>
                                                                    <TableCell><MDTypography variant="caption" color={p.isEntrada ? "success" : "text"}>{p.isEntrada ? (p.isMle ? "Entrada (MLE)" : "Entrada") : "Parcela"}</MDTypography></TableCell>
                                                                    <TableCell>
                                                                        <MDInput
                                                                            type="date"
                                                                            size="small"
                                                                            value={p.vencimento}
                                                                            onChange={(e) => handleChangeParcela(pIdx, 'vencimento', e.target.value)}
                                                                            sx={{ minWidth: 130 }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <MDInput
                                                                            type="text"
                                                                            size="small"
                                                                            value={typeof p.valorParcela === 'number' ? formatCurrencyInput(p.valorParcela) : p.valorParcela}
                                                                            onChange={(e) => handleChangeParcela(pIdx, 'valorParcela', formatCurrencyInput(e.target.value))}
                                                                            sx={{ minWidth: 120 }}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <MDBox p={2} textAlign="center" borderRadius="md" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa", border: darkMode ? `1px dashed ${ESCURO.borda}` : "1px dashed #cfd8dc" }}>
                                                    <MDTypography variant="caption" color="text">
                                                        Clique em <b>Calcular Parcelas</b> para simular o cronograma de vencimentos e valores de cada parcela.
                                                    </MDTypography>
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    </Grid>
                                )}

                                {/* Êxito do escritório que o acordo vai gravar (cadastro do cliente; ficha de honorários não tem) */}
                                {!isHonorariosTab && simulacaoData && (
                                    <Grid item xs={12}>
                                        <MDTypography variant="caption" color="text" display="block" px={1}>
                                            {simulacaoData.percentualExito !== null && simulacaoData.percentualExito !== undefined
                                                ? <>Êxito do escritório neste acordo: <b>{String(simulacaoData.percentualExito).replace(".", ",")}%</b> sobre cada parcela paga (cadastro do cliente; fica gravado no acordo).</>
                                                : "Cliente sem % de êxito cadastrado (Ferramentas > Clientes): o acordo fica sem êxito."}
                                        </MDTypography>
                                    </Grid>
                                )}

                                {/* Seção Honorários Advocatícios (se aplicável) */}
                                {!isHonorariosTab && (() => {
                                    const allowsHO = contratos.some(c => (selectedContratos.length === 0 || selectedContratos.includes(c.id)) && c.permiteGerarHonorario);
                                    if (!allowsHO) return null;
                                    return (
                                        <Grid item xs={12}>
                                            <MDBox p={2} borderRadius="lg" sx={{ backgroundColor: darkMode ? ESCURO.painel : "#ffffff", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e2e8f0" }}>
                                                <FormControlLabel
                                                    control={<Switch checked={gerarHO} onChange={(e) => setGerarHO(e.target.checked)} color="info" />}
                                                    label={<MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>Gerar Dívida de Honorários Advocatícios sobre o Acordo?</MDTypography>}
                                                />
                                                {gerarHO && (
                                                    <Grid container spacing={2} mt={0.5} alignItems="flex-start">
                                                        <Grid item xs={12} sm={6}>
                                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Percentual (%) sobre o Acordo</InputLabel>
                                                            <MDBox display="flex" gap={1} alignItems="center">
                                                                <MDInput
                                                                    size="small"
                                                                    value={hoPercentual}
                                                                    onChange={(e) => setHoPercentual(e.target.value)}
                                                                    disabled={!hoExcecao}
                                                                    placeholder={hoExcecao ? "Ex.: 15" : "-"}
                                                                    fullWidth
                                                                />
                                                                <MDButton
                                                                    variant="outlined"
                                                                    color={hoExcecao ? "secondary" : "warning"}
                                                                    size="small"
                                                                    sx={{ whiteSpace: "nowrap", minWidth: "auto", height: 38 }}
                                                                    onClick={() => { setHoExcecao(!hoExcecao); if (hoExcecao) setHoMotivo(""); }}
                                                                >
                                                                    {hoExcecao ? "Usar o da campanha" : "Exceção"}
                                                                </MDButton>
                                                            </MDBox>
                                                            <MDTypography variant="caption" color="text" display="block" px={1}>
                                                                {hoPercentualCampanha !== null
                                                                    ? `% da campanha ${simulacaoData?.campanhaNome || ""}: ${String(hoPercentualCampanha).replace(".", ",")}%.`
                                                                    : `A campanha ${simulacaoData?.campanhaNome || ""} não tem % de honorários: use Exceção para informar.`}
                                                                {hoPercentualNumero > 0 && ` Débito: ${toCurrency((acordoOpcaoSelecionada?.isManual ? (formatUncurrency(acordoValorManual) || 0) : (acordoOpcaoSelecionada?.totalComDesconto || 0)) * hoPercentualNumero / 100)}.`}
                                                            </MDTypography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Honorário</InputLabel>
                                                            <MDInput select size="small" value={hoTipo} onChange={(e) => setHoTipo(e.target.value)} fullWidth>
                                                                {hoTiposList.map((t) => (
                                                                    <MenuItem key={t.id} value={t.id}>
                                                                        {t.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </MDInput>
                                                        </Grid>
                                                        {hoExcecao && (
                                                            <Grid item xs={12}>
                                                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Motivo da exceção *</InputLabel>
                                                                <MDInput
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={2}
                                                                    value={hoMotivo}
                                                                    onChange={(e) => setHoMotivo(e.target.value)}
                                                                    placeholder="Por que este acordo não usa o % de honorários da campanha"
                                                                    inputProps={{ maxLength: 500 }}
                                                                />
                                                                <MDTypography variant="caption" color="text" display="block" px={1}>
                                                                    Vai na ocorrência do acordo, com o % usado e o da campanha.
                                                                </MDTypography>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                )}
                                            </MDBox>
                                        </Grid>
                                    );
                                })()}
                            </Grid>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ p: 2, backgroundColor: darkMode ? ESCURO.cartao : "#ffffff", borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5" }}>
                    <MDButton
                        variant="outlined"
                        color={darkMode ? "white" : "secondary"}
                        onClick={() => { setAcordoModalOpen(false); setAcordoMinimizado(false); }}
                        sx={{ px: 3 }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={handleGerarAcordo}
                        disabled={acordoLoading || isBloqueadoPorRegra || resimulandoVencimento || (!isAvistaAcordo && (!cronogramaCalculado || acordoParcelasGeradas.length === 0))}
                        startIcon={<Icon>check</Icon>}
                        sx={{ px: 3, fontWeight: "bold" }}
                    >
                        Confirmar e Gerar
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir o contrato <strong>{contratoToDelete?.titulo}</strong>?
                    <br /><br />
                    <MDTypography variant="caption" color="error">
                        Atenção: Esta ação não poderá ser desfeita e pode afetar acordos vinculados.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} color={darkMode ? "white" : "secondary"}>
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
            {/* Modal Confirmação Cancelamento */}
            <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Atenção! Contrato em acordo ativo</DialogTitle>
                <DialogContent>
                    {acordosSobrepostos.acordos.map(a => {
                        const { pagas, total } = parcelasDoAcordo(a);
                        const titulos = (a.contratos || []).map(c => c.titulo || "sem título");
                        // O backend cancela o acordo inteiro: contrato dele que não está nesta negociação volta à cobrança
                        const ficamFora = (a.contratos || []).filter(c => !acordosSobrepostos.ids.includes(c.idContrato)).map(c => c.titulo || "sem título");
                        return (
                            <MDTypography key={a.id} variant="body2" color="text" mb={1}>
                                <b>Acordo nº {a.id}</b>
                                {titulos.length > 0 && ` (${titulos.length === 1 ? "contrato" : "contratos"} ${titulos.join(", ")})`}: {pagas} de {total} parcelas pagas.
                                {ficamFora.length > 0 && (
                                    <> {ficamFora.length === 1 ? "O contrato" : "Os contratos"} <b>{ficamFora.join(", ")}</b> {ficamFora.length === 1 ? "não está nesta negociação e volta" : "não estão nesta negociação e voltam"} para a cobrança.</>
                                )}
                            </MDTypography>
                        );
                    })}
                    <MDTypography variant="body2" color="text" mt={1.5}>
                        Ao clicar em <b>"Confirmar e Gerar"</b>, {acordosSobrepostos.acordos.length > 1 ? "esses acordos serão cancelados" : "esse acordo será cancelado"} (parcelas
                        em aberto e boletos) e o contrato entra no novo acordo pelo saldo atual, já descontado o que foi pago. Até lá, nada muda.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                        Para negociar só os outros contratos, volte, marque apenas eles na tabela e simule de novo.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setConfirmCancelOpen(false)} color="secondary">Voltar</MDButton>
                    <MDButton onClick={() => {
                        setConfirmCancelOpen(false);
                        if (pendingOpcao && pendingOpcao.isManual) {
                            proceedOpenAcordoManual();
                        } else {
                            proceedOpenAcordoModal(pendingOpcao);
                        }
                    }} color="info">Continuar e renegociar</MDButton>
                </DialogActions>
            </Dialog>

            {/* Simular a ficha completa com parte dos contratos em acordo: negociar só os de fora ou renegociar o acordo */}
            <Dialog open={Boolean(escolhaAcordo)} onClose={() => setEscolhaAcordo(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Parte dos contratos já está em acordo</DialogTitle>
                {escolhaAcordo && (() => {
                    const d = escolhaAcordo.dentro.length;
                    const f = escolhaAcordo.fora.length;
                    const varios = escolhaAcordo.acordos.length > 1;
                    const numeros = escolhaAcordo.acordos.map(a => a.id).join(", ");
                    const parcelas = escolhaAcordo.acordos.map(a => {
                        const { pagas, total } = parcelasDoAcordo(a);
                        return varios ? `nº ${a.id}: ${pagas} de ${total} pagas` : `${pagas} de ${total} parcelas pagas`;
                    }).join("; ");
                    return (
                        <>
                            <DialogContent>
                                <MDTypography variant="body2" color="text" mb={1.5}>
                                    {d === 1 ? "O contrato" : "Os contratos"} <b>{escolhaAcordo.dentro.map(c => c.titulo || "sem título").join(", ")}</b>{" "}
                                    {d === 1 ? "está" : "estão"} {varios ? "nos acordos" : "no acordo"} nº {numeros} ({parcelas}) e{" "}
                                    {d === 1 ? "fica suspenso" : "ficam suspensos"} até a quitação ou o cancelamento.
                                </MDTypography>
                                <MDTypography variant="body2" color="text" mb={0.5}>
                                    <b>Negociar só {f === 1 ? "o contrato fora do acordo" : `os ${f} contratos fora do acordo`}:</b>{" "}
                                    {escolhaAcordo.fora.map(c => c.titulo || "sem título").join(", ")}. {varios ? "Os acordos atuais continuam" : "O acordo atual continua"} valendo.
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                    <b>Incluir e renegociar:</b> todos os contratos vão para a simulação; ao gerar o novo acordo,{" "}
                                    {varios ? `os acordos nº ${numeros} são cancelados` : `o acordo nº ${numeros} é cancelado`} (parcelas em aberto e boletos)
                                    e o contrato entra pelo saldo atual, já descontado o que foi pago.
                                </MDTypography>
                            </DialogContent>
                            <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
                                <MDButton onClick={() => setEscolhaAcordo(null)} color="secondary">Voltar</MDButton>
                                <MDButton
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => {
                                        setEscolhaAcordo(null);
                                        simularContratos(contratos.map(c => c.id), true);
                                    }}
                                >
                                    Incluir e renegociar
                                </MDButton>
                                <MDButton
                                    variant="contained"
                                    color="info"
                                    onClick={() => {
                                        const ids = escolhaAcordo.fora.map(c => c.id);
                                        setEscolhaAcordo(null);
                                        setSelectedContratos(ids);
                                        simularContratos(ids, false);
                                    }}
                                >
                                    Negociar só {f === 1 ? "o contrato fora do acordo" : `os ${f} fora do acordo`}
                                </MDButton>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {/* Migração / Devolução Modal */}
            <Dialog open={migracaoModalOpen} onClose={() => setMigracaoModalOpen(false)} maxWidth={destinoJuridico ? "md" : "sm"} fullWidth>
                <DialogTitle>
                    {tipoSolicitacao === "MIGRACAO"
                        ? (isDirectMigrateUser ? "Migrar Contratos Selecionados" : "Solicitar Migração de Carteira (Fila do Suporte)")
                        : (isDirectMigrateUser ? "Devolver Ficha / Contrato" : "Solicitar Devolução (Fila do Suporte)")
                    }
                </DialogTitle>
                <DialogContent>
                    <MDBox display="flex" gap={1.5} mb={2.5} mt={0.5}>
                        <MDButton
                            variant={tipoSolicitacao === "MIGRACAO" ? "gradient" : "outlined"}
                            color="info"
                            size="small"
                            fullWidth
                            onClick={() => setTipoSolicitacao("MIGRACAO")}
                        >
                            <Icon sx={{ mr: 0.5 }}>autorenew</Icon> Migração de Carteira
                        </MDButton>
                        <MDButton
                            variant={tipoSolicitacao === "DEVOLUCAO" ? "gradient" : "outlined"}
                            color="warning"
                            size="small"
                            fullWidth
                            onClick={() => setTipoSolicitacao("DEVOLUCAO")}
                        >
                            <Icon sx={{ mr: 0.5 }}>assignment_return</Icon> Devolução
                        </MDButton>
                    </MDBox>

                    {tipoSolicitacao === "MIGRACAO" ? (
                        <>
                            <MDTypography variant="body2" color="text" mb={2}>
                                {!isDirectMigrateUser
                                    ? "Preencha a carteira de destino para enviar a solicitação à Fila do Suporte:"
                                    : contratosFiltrados.length === 1
                                        ? "Selecione as opções de destino para a ficha:"
                                        : (selectedContratos.length > 0 && selectedContratos.length < contratosFiltrados.length)
                                            ? `Selecione as opções de destino do(s) ${selectedContratos.length} contrato(s) selecionado(s):`
                                            : `Selecione as opções de destino para todos os ${contratosFiltrados.length} contratos:`
                                }
                            </MDTypography>
                            <MDInput
                                select
                                fullWidth
                                label="Carteira de Destino *"
                                value={selectedCarteiraMigracao}
                                onChange={(e) => setSelectedCarteiraMigracao(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="">Selecione...</MenuItem>
                                {carteirasMigracaoOptions.map((c, i) => (
                                    <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                                ))}
                            </MDInput>

                            {(isDirectMigrateUser || destinoJuridico) && (
                                <>
                                    <MDInput
                                        select
                                        fullWidth
                                        label={isDirectMigrateUser ? "Proprietário da Ficha (Opcional)" : "Responsável / negociador (sugestão para o suporte)"}
                                        value={selectedUsuarioMigracao}
                                        onChange={(e) => setSelectedUsuarioMigracao(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mb: 2 }}
                                    >
                                        <MenuItem value="">Nenhum</MenuItem>
                                        {usuariosOptions.map((c, i) => (
                                            <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                                        ))}
                                    </MDInput>
                                    <MDInput
                                        select
                                        fullWidth
                                        label={isDirectMigrateUser ? "Status da Ficha (Opcional)" : "Status da ficha (sugestão para o suporte)"}
                                        value={selectedStatusMigracao}
                                        onChange={(e) => setSelectedStatusMigracao(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mb: 2 }}
                                    >
                                        <MenuItem value="">{isDirectMigrateUser ? "Em aberto (Padrão)" : "Manter o status atual"}</MenuItem>
                                        {statusFichaOptions.map((c, i) => (
                                            <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                                        ))}
                                    </MDInput>
                                </>
                            )}
                            {destinoJuridico && (
                                <PacoteJuridicoMigracao dados={dadosCodigoMigracao} valor={pacoteMigracao} onChange={setPacoteMigracao} carteiraId={selectedCarteiraMigracao}
                                    ajuda="Deixe em branco o que não se aplica. Sem nada preenchido, a ficha vai para Migrações para o Jurídico e o CobJur completa depois." />
                            )}
                        </>
                    ) : (
                        <MDBox mb={2}>
                            <MDBox
                                p={1.5}
                                mb={2}
                                sx={{
                                    borderRadius: "8px",
                                    bgcolor: darkMode ? "#4a3b1a" : "#fff8e1",
                                    border: `1px solid ${darkMode ? "#ffa000" : "#ffe082"}`
                                }}
                            >
                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffb74d" : "#e65100", display: "block" }}>
                                    {selectedContratos.length > 0 && selectedContratos.length < contratosFiltrados.length
                                        ? `Atenção: A devolução será aplicada para ${selectedContratos.length} contrato(s) selecionado(s).`
                                        : `Atenção: A devolução será solicitada para toda a ficha do devedor (${contratosFiltrados.length} contrato(s)).`
                                    }
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    )}

                    <MDBox mb={2}>
                        <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.8}>
                            Anexar Documento / Comprovante (Opcional):
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                sx={{
                                    borderColor: darkMode ? "rgba(255,255,255,0.25)" : "#ccc",
                                    color: darkMode ? "#fff" : "inherit",
                                    fontSize: "0.75rem",
                                    textTransform: "none"
                                }}
                            >
                                <Icon sx={{ mr: 0.5, fontSize: "18px !important" }}>attach_file</Icon>
                                {migracaoFile ? "Substituir Arquivo" : "Escolher Arquivo"}
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => setMigracaoFile(e.target.files[0] || null)}
                                />
                            </Button>
                            {migracaoFile && (
                                <Chip
                                    label={migracaoFile.name}
                                    onDelete={() => setMigracaoFile(null)}
                                    size="small"
                                    color="info"
                                    sx={{ maxWidth: "250px" }}
                                />
                            )}
                        </MDBox>
                    </MDBox>

                    <MDInput
                        fullWidth
                        multiline
                        rows={3}
                        label={tipoSolicitacao === "MIGRACAO" ? "Observação para o Suporte" : "Motivo da Devolução *"}
                        placeholder={tipoSolicitacao === "MIGRACAO" ? "Descreva a justificativa ou necessidade da migração..." : "Informe o motivo detalhado da devolução..."}
                        value={observacaoMigracao}
                        onChange={(e) => setObservacaoMigracao(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required={tipoSolicitacao === "DEVOLUCAO"}
                    />
                </DialogContent>
                <DialogActions>
                    <MDButton color="secondary" onClick={() => setMigracaoModalOpen(false)}>Cancelar</MDButton>
                    <MDButton color={tipoSolicitacao === "MIGRACAO" ? "info" : "warning"} onClick={handleConfirmMigracao}>
                        {tipoSolicitacao === "MIGRACAO"
                            ? (isDirectMigrateUser ? "Migrar" : "Enviar Solicitação")
                            : (isDirectMigrateUser ? "Devolver Diretamente" : "Enviar Solicitação")
                        }
                    </MDButton>
                </DialogActions>
            </Dialog>

            {!isViewOnly && formRef && (
                <Grid container spacing={3} mt={2}>
                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <MDButton variant="gradient" color="info" disabled={isSavingForm || formRef.current?.isSubmitting} onClick={async () => {
                            if (formRef.current) {
                                setIsSavingForm(true);
                                const errors = await formRef.current.validateForm();
                                if (Object.keys(errors).length > 0) {
                                    formRef.current.setTouched(
                                        Object.keys(errors).reduce((acc, curr) => ({ ...acc, [curr]: true }), {})
                                    );
                                    alert("Existem campos obrigatórios na aba Cadastro Básico que não foram preenchidos.");
                                    setIsSavingForm(false);
                                    return;
                                }
                                formRef.current.submitForm().finally(() => setIsSavingForm(false));
                            }
                        }}>
                            Salvar Cadastro
                        </MDButton>
                    </Grid>
                </Grid>
            )}
            <DevolucaoDiretaDialog alvo={devolucaoDireta} onClose={() => setDevolucaoDireta(null)} onConcluida={aoConcluirDevolucaoDireta} />

            <Dialog open={confirmAction.open} onClose={() => setConfirmAction({ ...confirmAction, open: false })} maxWidth="xs" fullWidth>
                <DialogTitle>{confirmAction.title}</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">{confirmAction.message}</MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => {
                        if (confirmAction.onCancel) confirmAction.onCancel();
                        setConfirmAction({ ...confirmAction, open: false });
                    }} color="secondary">{confirmAction.cancelText || "Cancelar"}</MDButton>
                    <MDButton onClick={() => {
                        if (confirmAction.onConfirm) confirmAction.onConfirm();
                        setConfirmAction({ ...confirmAction, open: false });
                    }} color={confirmAction.confirmColor || "info"}>{confirmAction.confirmText || "OK"}</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={openExportCronogramaModal} onClose={() => setOpenExportCronogramaModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Exportar Cronograma (Amostra)</DialogTitle>
                <DialogContent>
                    <MDBox mt={2} mb={2}>
                        <MDTypography variant="body2" mb={2}>
                            Selecione a data da primeira parcela. As demais datas serão refletidas sequencialmente de acordo com a periodicidade ({acordoPeriodicidade} dias), mas os valores continuarão sendo exatamente os da simulação atual.
                        </MDTypography>
                        <MDInput
                            type="date"
                            label="Data da 1ª Parcela"
                            value={exportDataPrimeiraParcela}
                            onChange={(e) => setExportDataPrimeiraParcela(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setOpenExportCronogramaModal(false)} color="secondary">Cancelar</MDButton>
                    <MDButton
                        color="info"
                        onClick={() => {
                            if (!acordoParcelasGeradas || acordoParcelasGeradas.length === 0) return;

                            const startDateStr = exportDataPrimeiraParcela;

                            const exportParcelas = acordoParcelasGeradas.map((p, idx) => ({
                                ...p,
                                vencimentoExport: vencimentoDaParcela(startDateStr, idx, acordoPeriodicidade).format("DD/MM/YYYY")
                            }));

                            const printWindow = window.open('', '', 'width=900,height=750');
                            if (!printWindow) {
                                window.print();
                                setOpenExportCronogramaModal(false);
                                return;
                            }

                            const rowsHtml = exportParcelas.map((p) => {
                                const tipoStr = p.isEntrada ? (p.isMle ? "Entrada (MLE)" : "Entrada") : "Parcela";
                                const valStr = typeof p.valorParcela === 'number' ? toCurrency(p.valorParcela) : (String(p.valorParcela).includes('R$') ? String(p.valorParcela) : 'R$ ' + String(p.valorParcela));
                                return `
                                    <tr>
                                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: center;">${p.numeroParcela}</td>
                                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${tipoStr}</td>
                                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: center;">${p.vencimentoExport}</td>
                                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: right;">${valStr}</td>
                                    </tr>
                                `;
                            }).join('');

                            printWindow.document.write(`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                    <meta charset="utf-8" />
                                    <title>Cronograma de Pagamento</title>
                                    <style>
                                        @page { size: auto; margin: 0; }
                                        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                                        html, body { background: #fff !important; margin: 0 !important; padding: 15mm 20mm !important; box-sizing: border-box; }
                                        body { font-family: 'Roboto', 'Calibri', 'Segoe UI', Helvetica, Arial, sans-serif; color: #000; }
                                        h2 { text-align: center; margin-bottom: 20px; }
                                        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; border: 1px solid #000; }
                                        th { background-color: #f0f0f0 !important; color: #000 !important; font-weight: bold; padding: 8px 12px; border: 1px solid #000; text-align: center; }
                                        td { border: 1px solid #000; padding: 8px 12px; }
                                    </style>
                                </head>
                                <body>
                                    <h2>Cronograma de Pagamento (Amostra)</h2>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Nº</th>
                                                <th>Tipo</th>
                                                <th>Vencimento</th>
                                                <th>Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${rowsHtml}
                                        </tbody>
                                    </table>
                                </body>
                                </html>
                            `);
                            printWindow.document.close();
                            setOpenExportCronogramaModal(false);
                        }}
                    >
                        Confirmar e Exportar
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Reagrupamento de Débitos */}
            <Dialog 
                open={reagruparModalOpen} 
                onClose={() => !isSubmittingReagrupamento && setReagruparModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? ESCURO.painel : "#ffffff",
                        borderRadius: "12px",
                        border: darkMode ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e0e0e0",
                        boxShadow: darkMode ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 32px rgba(0,0,0,0.12)",
                        overflow: "hidden"
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between",
                    py: 1.8, 
                    px: 2.5,
                    borderBottom: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "#f0f2f5"}`,
                    bgcolor: darkMode ? ESCURO.cartao : "#f8f9fa"
                }}>
                    <MDBox display="flex" alignItems="center" gap={1.2}>
                        <MDBox 
                            sx={{ 
                                width: 32, 
                                height: 32, 
                                borderRadius: "8px", 
                                bgcolor: darkMode ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd", 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "center" 
                            }}
                        >
                            <Icon sx={{ color: "#2196f3", fontSize: "20px !important" }}>merge</Icon>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                            Reagrupar Débitos entre Fichas
                        </MDTypography>
                    </MDBox>
                    <IconButton 
                        size="small" 
                        onClick={() => !isSubmittingReagrupamento && setReagruparModalOpen(false)}
                        sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : "#777" }}
                    >
                        <Icon fontSize="small">close</Icon>
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, borderColor: darkMode ? "rgba(255,255,255,0.1)" : "#f0f2f5" }}>
                    {/* Ficha de Origem */}
                    <MDBox mb={2.5}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "rgba(255,255,255,0.75)" : "#555", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.72rem", mb: 0.8, display: "block" }}>
                            Ficha de Origem (Atual)
                        </MDTypography>
                        <MDBox 
                            p={1.5} 
                            sx={{ 
                                bgcolor: darkMode ? ESCURO.cartao : "#f8f9fa", 
                                borderRadius: "8px",
                                border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#e0e0e0"}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1
                            }}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <Chip 
                                    label={`Ficha #${selectedFicha?.rawId || selectedFicha?.id || "-"}`} 
                                    size="small" 
                                    color="info" 
                                    sx={{ fontWeight: "bold", fontSize: "11px", height: "22px" }} 
                                />
                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    {selectedFicha?.nomeCarteira || "Carteira Principal"}
                                </MDTypography>
                            </MDBox>
                            <Chip 
                                icon={<Icon fontSize="small" sx={{ color: "inherit !important" }}>receipt_long</Icon>}
                                label={`${contratosFiltrados.length} ${contratosFiltrados.length === 1 ? "débito" : "débitos"}`} 
                                size="small" 
                                variant="outlined"
                                color={darkMode ? "secondary" : "default"}
                                sx={{ fontWeight: "medium", fontSize: "11px", height: "22px" }}
                            />
                        </MDBox>
                    </MDBox>

                    {/* Ficha de Destino */}
                    <MDBox mb={2.5}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "rgba(255,255,255,0.75)" : "#555", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.72rem", mb: 0.8, display: "block" }}>
                            Ficha de Destino (para onde os débitos irão)
                        </MDTypography>
                        {fichasDisponiveis.length === 0 ? (
                            <MDBox p={1.5} sx={{ bgcolor: darkMode ? "#4a2525" : "#ffebee", borderRadius: "8px", border: "1px solid #ef5350" }}>
                                <MDTypography variant="caption" color="error" fontWeight="medium">
                                    Este devedor não possui outra ficha cadastrada para onde transferir os débitos.
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(o) => o.rawId || o.id}
                                getOptionLabel={(o) => `[Ficha #${o.rawId || o.id}] ${o.nomeCarteira || (o.isHonorario ? 'Honorários' : 'Carteira')} (${o.situacao === 1 ? 'Devolvida' : (o.situacao === 7 ? 'Quitada' : 'Ativa')})`}
                                placeholder="Selecione a ficha de destino..."
                                value={fichasDisponiveis.find(f => String(f.rawId || f.id) === String(reagruparDestinoId)) || null}
                                options={fichasDisponiveis}
                                onChange={(val) => setReagruparDestinoId(val ? (val.rawId || val.id) : "")}
                            />
                        )}
                    </MDBox>

                    {/* Escopo da Transferência */}
                    <MDBox mb={2.5}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "rgba(255,255,255,0.75)" : "#555", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.72rem", mb: 0.8, display: "block" }}>
                            Escopo da Transferência
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1.2}>
                            {/* Opção 1: Todos */}
                            <MDBox
                                onClick={() => setReagruparMoverTodos(true)}
                                p={1.5}
                                borderRadius="8px"
                                sx={{
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    bgcolor: reagruparMoverTodos ? (darkMode ? ESCURO.cartao : "#e3f2fd") : (darkMode ? ESCURO.painelEscuro : "#f8f9fa"),
                                    border: reagruparMoverTodos 
                                        ? "2px solid #1976d2" 
                                        : `1px solid ${darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0"}`,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.2
                                }}
                            >
                                <Radio
                                    checked={reagruparMoverTodos}
                                    onChange={() => setReagruparMoverTodos(true)}
                                    color="info"
                                    sx={{ p: 0, mt: 0.2 }}
                                />
                                <MDBox display="flex" flexDirection="column" gap={0.3}>
                                    <MDTypography variant="button" fontWeight="bold" color={reagruparMoverTodos ? "info" : (darkMode ? "white" : "dark")}>
                                        Mover TODOS os débitos desta ficha ({contratosFiltrados.length})
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}>
                                        <strong>Consolidação total:</strong> migrará contratos, acordos, históricos e dados de processos. A ficha de origem será <strong>eliminada</strong> sem pendências relacionais.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {/* Opção 2: Parcial */}
                            <MDBox
                                onClick={() => {
                                    if (selectedContratos.length > 0) setReagruparMoverTodos(false);
                                }}
                                p={1.5}
                                borderRadius="8px"
                                sx={{
                                    cursor: selectedContratos.length > 0 ? "pointer" : "not-allowed",
                                    opacity: selectedContratos.length > 0 ? 1 : 0.6,
                                    transition: "all 0.2s ease",
                                    bgcolor: !reagruparMoverTodos ? (darkMode ? ESCURO.cartao : "#e3f2fd") : (darkMode ? ESCURO.painelEscuro : "#f8f9fa"),
                                    border: !reagruparMoverTodos 
                                        ? "2px solid #1976d2" 
                                        : `1px solid ${darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0"}`,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.2
                                }}
                            >
                                <Radio
                                    checked={!reagruparMoverTodos}
                                    onChange={() => {
                                        if (selectedContratos.length > 0) setReagruparMoverTodos(false);
                                    }}
                                    disabled={selectedContratos.length === 0}
                                    color="info"
                                    sx={{ p: 0, mt: 0.2 }}
                                />
                                <MDBox display="flex" flexDirection="column" gap={0.3}>
                                    <MDTypography variant="button" fontWeight="bold" color={!reagruparMoverTodos ? "info" : (selectedContratos.length === 0 ? "secondary" : (darkMode ? "white" : "dark"))}>
                                        Mover apenas os {selectedContratos.length} débito(s) selecionado(s)
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}>
                                        {selectedContratos.length === 0
                                            ? "(Nenhum débito marcado na tabela. Para usar esta opção, selecione as checkboxes da tabela de contratos)."
                                            : "<strong>Migração parcial:</strong> apenas os débitos selecionados migrarão, e esta ficha continuará ativa com os demais débitos."}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </MDBox>

                    {/* Motivo / Justificativa */}
                    <MDBox mb={1}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "rgba(255,255,255,0.75)" : "#555", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.72rem", mb: 0.8, display: "block" }}>
                            Motivo / Justificativa (Opcional)
                        </MDTypography>
                        <MDInput
                            placeholder="Ex: Correção de migração legada duplicada por engano"
                            value={reagruparMotivo}
                            onChange={(e) => setReagruparMotivo(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </MDBox>
                </DialogContent>

                <DialogActions sx={{ 
                    p: 2, 
                    px: 2.5,
                    borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "#f0f2f5"}`,
                    bgcolor: darkMode ? ESCURO.cartao : "#f8f9fa",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1
                }}>
                    <MDButton 
                        variant="outlined" 
                        color={darkMode ? "white" : "secondary"} 
                        size="small" 
                        onClick={() => setReagruparModalOpen(false)} 
                        disabled={isSubmittingReagrupamento}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        disabled={isSubmittingReagrupamento || !reagruparDestinoId || fichasDisponiveis.length === 0 || (!reagruparMoverTodos && selectedContratos.length === 0)}
                        onClick={handleConfirmReagrupar}
                    >
                        {isSubmittingReagrupamento ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <>
                                <Icon sx={{ mr: 0.5 }}>merge</Icon>
                                Confirmar Reagrupamento
                            </>
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
